import {ICDCodeGroupService} from '../@core/icd-groups.service';
import {Diagnosis} from '../models/diagnosis';
import {Hl7Message} from '../models/hl7-message';
import {Human} from '../models/human';
import {ICDCode} from '../models/ICD-code';
import {
    Allergy,
    ClinicalResult,
    CriticalVital,
    ImagingResult,
    Immunization,
    LabReport,
    LabTest,
    Order,
    PatientEvent,
    Procedure,
    ProgressNote,
    Task,
    TreatmentEntry,
    Wound,
} from '../models/models';
import {Patient} from '../models/patient';
import {SnomedConcept} from '../models/snomed-concept';
import {Visit} from '../models/visit';
import {MenuItemDefinition} from '../portal/sidebar/menu-item';
import {limitText} from '../utils/string.utils';
import {getId} from '../utils/type.utils';
import {ContentDefinition, ReviewDefinition, SlidePanelDefinition, SmartLinkDefinition} from './definitions-base';
import {Claim} from '../models/claim';
import {Remittance} from '../models/remittance';

export class ContentDefinitions {
    static readonly ICD_CODE = new ContentDefinition({
        key: 'ICD_CODE',
        name: {lowerCase: 'ICD-10 code', capitalized: 'ICD-10 Code'},
        icon: 'tag',
        loadComponent: () => import('../portal/@slide-panel-preview/icd-code-preview/icd-code-preview.component').then(m => m.IcdCodePreviewComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/icd-code-preview/icd-code-preview.module').then(m => m.IcdCodePreviewModule),
        modelBase: ICDCode,
        actionEntryKey: 'icd',
    });
    static readonly SNOMED_CONCEPT = new ContentDefinition({
        key: 'SNOMED_CONCEPT',
        name: {lowerCase: 'snomed concept', capitalized: 'SNOMED Concept'},
        icon: 'tag',
        modelBase: SnomedConcept,
    });
    static readonly PATIENT = new ContentDefinition({
        key: 'PATIENT',
        path: 'patients/:id',
        icon: 'address-card',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-details.component').then(m => m.PatientDetailsComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/patient-details.module').then(m => m.PatientDetailsModule),
    });
    static readonly LAB_REPORT = new ContentDefinition<LabReport>({
        key: 'LAB_REPORT',
        name: {lowerCase: 'lab report', capitalized: 'Lab Report'},
        path: 'care-surveillance/lab-reports/:id',
        icon: 'vials',
        loadComponent: () => import('../portal/@slide-panel-preview/lab-report/lab-report.component').then(m => m.LabReportComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-report/lab-report.module').then(m => m.LabReportModule),
        modelBase: LabReport,
        actionEntryKey: 'lab_test',
    });
    static readonly LAB_TEST = new ContentDefinition<LabTest>({
        key: 'LAB_TEST',
        path: 'care-surveillance/lab-reports/test/:id',
        icon: 'vials',
        loadComponent: () => import('../portal/@slide-panel-preview/lab-test/lab-test.component').then(m => m.LabTestComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-test/lab-test.module').then(m => m.LabTestModule),
        modelBase: LabTest,
        actionEntryKey: 'lab_test',
        patientDetailsSubPath: '?tab=results&subTab=labs',
    });
    static readonly IMAGING_RESULT = new ContentDefinition({
        key: 'IMAGING_RESULT',
        path: 'care-surveillance/imaging-results/:id',
        icon: 'x-ray',
        loadComponent: () => import('../portal/@slide-panel-preview/imaging-result/imaging-result.component').then(m => m.ImagingResultComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/imaging-result/imaging-result.module').then(m => m.ImagingResultModule),
        modelBase: ImagingResult,
        actionEntryKey: 'imaging_result',
        patientDetailsSubPath: '?tab=results&subTab=imaging',
    });
    static readonly CLINICAL_RESULT = new ContentDefinition({
        key: 'CLINICAL_RESULT',
        modelBase: ClinicalResult,
        actionEntryKey: 'clinical_test_result',
        patientDetailsSubPath: '?tab=results&subTab=clinical_results',
    });
    static readonly EVENT = new ContentDefinition<PatientEvent>({
        key: 'EVENT',
        name: {lowerCase: 'fall', capitalized: 'Fall'},
        path: 'patients/event/:id',
        icon: 'star-shooting',
        loadComponent: () => import('../portal/@slide-panel-preview/event-preview/event-preview.component').then(m => m.EventPreviewComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/event-preview/event-preview.module').then(m => m.EventPreviewModule),
        modelBase: PatientEvent,
        actionEntryKey: 'event',
        patientDetailsSubPath: '?tab=events',
    });
    static readonly VITAL = new ContentDefinition<CriticalVital>({
        key: 'VITAL',
        name: {lowerCase: 'critical vital', capitalized: 'Critical Vital'},
        path: 'patients/vital/:id',
        icon: 'heartbeat',
        loadComponent: () => import('../portal/@slide-panel-preview/vital-preview/vital-preview.component').then(m => m.VitalPreviewComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/vital-preview/vital-preview.module').then(m => m.VitalPreviewModule),
        modelBase: CriticalVital,
        actionEntryKey: 'vital',
    });
    static readonly CRITICAL_VITALS = new ContentDefinition<CriticalVital>({
        key: 'CRITICAL_VITALS',
        name: {lowerCase: 'critical vitals', capitalized: 'Critical Vitals'},
        icon: 'heartbeat',
        // this is commented out because there is problem with the chart's tooltip if it's lazy loaded: https://github.com/swimlane/ngx-charts/issues/1733
        // loadComponent: () => import('../portal/@slide-panel-preview/critical-vitals-preview/critical-vitals-preview.component').then(m => m.CriticalVitalsPreviewComponent),
        // loadChildren: () => import('../portal/@slide-panel-preview/critical-vitals-preview/critical-vitals-preview.module').then(m => m.CriticalVitalsPreviewModule),
        modelBase: CriticalVital,
    });
    static readonly MEDICATION = new ContentDefinition<TreatmentEntry>({
        key: 'MEDICATION',
        path: 'care-surveillance/medications/:id',
        icon: 'prescription-bottle',
        loadComponent: () => import('../portal/@slide-panel-preview/medication-preview/medication-preview.component').then(m => m.MedicationPreviewComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/medication-preview/medication-preview.module').then(m => m.MedicationPreviewModule),
        modelBase: TreatmentEntry,
        actionEntryKey: 'treatment_history',
        patientDetailsSubPath: '?tab=medications',
    });
    static readonly DIAGNOSIS = new ContentDefinition<Diagnosis>({
        key: 'DIAGNOSIS',
        icon: 'stethoscope',
        modelBase: Diagnosis,
        actionEntryKey: 'diagnosis',
        patientDetailsSubPath: '?tab=diagnoses',
    });
    static readonly PATIENT_ICD = new ContentDefinition({
        key: 'PATIENT_ICD',
        path: 'patients/:patientId/icd/:id',
        icon: 'stethoscope',
    });
    static readonly PATIENT_SNOMED = new ContentDefinition({
        key: 'PATIENT_SNOMED',
        path: 'patients/:patientId/snomed/:id',
        icon: 'stethoscope',
        permissions: ['PERMISSION_READ_MEDICATION_LIST'],
    });
    static readonly OTHER_ORDER = new ContentDefinition({
        key: 'OTHER_ORDER',
        name: {lowerCase: 'EMR order', capitalized: 'EMR Order'},
        icon: 'clipboard-list-check',
        modelBase: Order,
        actionEntryKey: 'order',
        patientDetailsSubPath: '?tab=orders&subTab=emr-orders',
    });
    static readonly ALLERGY = new ContentDefinition({
        key: 'ALLERGY',
        modelBase: Allergy,
        patientDetailsSubPath: '/allergies',
        actionEntryKey: 'patient_allergy',
    });
    static readonly IMMUNIZATION = new ContentDefinition({
        key: 'IMMUNIZATION',
        modelBase: Immunization,
        patientDetailsSubPath: '/immunizations',
        actionEntryKey: 'immunization',
    });
    static readonly PROCEDURE = new ContentDefinition({
        key: 'PROCEDURE',
        modelBase: Procedure,
        patientDetailsSubPath: '/procedures',
        actionEntryKey: 'procedure',
    });
    static readonly TASK = new ContentDefinition({
        key: 'TASK',
        name: {lowerCase: 'note', capitalized: 'Note'},
        path: 'arsana-notes/:id',
        icon: 'comments',
        loadComponent: () => import('../portal/@slide-panel-preview/ccm-note/ccm-note.component').then(m => m.CcmNoteComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/ccm-note/ccm-note.module').then(m => m.CcmNoteModule),
        modelBase: Task,
    });
    static readonly TASK_PROPOSAL = new ContentDefinition({
        key: 'TASK_PROPOSAL',
        name: {lowerCase: 'visit proposal', capitalized: 'Visit Proposal'},
        icon: 'stethoscope',
        path: 'task-proposal/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/task-proposal-preview/task-proposal-preview.component').then(m => m.TaskProposalPreviewComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/task-proposal-preview/task-proposal-preview.module').then(m => m.TaskProposalPreviewModule),
    });
    static readonly VISIT = new ContentDefinition({
        key: 'VISIT',
        name: {lowerCase: 'visit occasion', capitalized: 'Visit Occasion'},
        path: 'scheduler/visits/:id',
        icon: 'clipboard-list-check',
        modelBase: Visit,
    });
    static readonly LAB_SHIPMENT = new ContentDefinition({
        key: 'LAB_SHIPMENT',
        name: {lowerCase: 'testing order', capitalized: 'Testing Order'},
        icon: 'dolly',
        path: 'covid-testing/shipments/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/lab-shipment-panel/lab-shipment-panel.component').then(m => m.LabShipmentPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-shipment-panel/lab-shipment-panel.module').then(m => m.LabShipmentPanelModule),
    });
    static readonly LAB_SHIPMENT_BOX = new ContentDefinition({
        key: 'LAB_SHIPMENT_BOX',
        name: {lowerCase: 'box', capitalized: 'Box'},
        icon: 'box-alt',
        path: 'covid-testing/shipments/boxes/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/lab-shipment-box-panel/lab-shipment-box-panel.component').then(m => m.LabShipmentBoxPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-shipment-box-panel/lab-shipment-box-panel.module').then(m => m.LabShipmentBoxPanelModule),
    });
    static readonly LAB_SHIPMENT_BAG = new ContentDefinition({
        key: 'LAB_SHIPMENT_BAG',
        name: {lowerCase: 'bag', capitalized: 'Bag'},
        icon: 'dolly',
        path: 'covid-testing/shipments/bags/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/lab-shipment-bag-panel/lab-shipment-bag-panel.component').then(m => m.LabShipmentBagPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-shipment-bag-panel/lab-shipment-bag-panel.module').then(m => m.LabShipmentBagPanelModule),
    });
    static readonly LAB_SHIPMENT_ORDER = new ContentDefinition({
        key: 'LAB_SHIPMENT_ORDER',
        name: {lowerCase: 'lab order', capitalized: 'Lab Order'},
        icon: 'vial',
        path: 'patients/lab-order/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/lab-shipment-order-panel/lab-shipment-order-panel.component').then(m => m.LabShipmentOrderPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-shipment-order-panel/lab-shipment-order-panel.module').then(m => m.LabShipmentOrderPanelModule),
    });
    static readonly ADD_PATIENT_TO_BST_DIALOG = new ContentDefinition({
        key: 'ADD_PATIENT_TO_BST_DIALOG',
        name: {lowerCase: 'add Patient to BroadStreet', capitalized: 'Add Patient to BroadStreet'},
        icon: 'check-circle',
        loadComponent: () => import('../portal/@portal-shared/add-patient-to-bst-dialog/add-patient-to-bst-dialog.component').then(m => m.AddPatientToBstDialogComponent),
    });
    static readonly PERSON_EDIT = new ContentDefinition({
        key: 'PERSON_EDIT',
        name: {lowerCase: 'person edit', capitalized: 'Person Edit'},
        icon: 'check',
        path: 'admin/patient-identification/:patientId/person/:id',
    });
    static readonly PERSON_LINK = new ContentDefinition({
        key: 'PERSON_LINK',
        name: {lowerCase: 'person merge', capitalized: 'Person Merge'},
        permissions: ['PERMISSION_WRITE_PERSONS'],
    });
    static readonly IMPORT_ACCOUNT = new ContentDefinition({
        key: 'IMPORT_ACCOUNT',
        path: 'system-settings/import-accounts/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/import-account-edit/import-account-edit.component').then(m => m.ImportAccountEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/import-account-edit/import-account-edit.module').then(m => m.ImportAccountEditModule),
    });
    static readonly IMPORT_ACCOUNT_NEW = new ContentDefinition({
        ...this.IMPORT_ACCOUNT,
        key: 'IMPORT_ACCOUNT_NEW',
        path: 'system-settings/import-accounts/new',
    });
    static readonly IMPORT_MANAGER = new ContentDefinition({
        key: 'IMPORT_MANAGER',
        path: 'system-settings/import-accounts/:id/import-manager',
        name: {lowerCase: 'import manager', capitalized: 'Import Manager'},
        loadComponent: () => import('../portal/admin/import-manager/import-manager.component').then(m => m.ImportManagerComponent),
        loadChildren: () => import('../portal/admin/import-manager/import-manager.module').then(m => m.ImportManagerModule),
    });
    static readonly CALENDAR_EVENT = new ContentDefinition({
        key: 'CALENDAR_EVENT',
        name: {lowerCase: 'calendar event', capitalized: 'calendar event'},
        icon: 'calendar-times',
        loadComponent: () => import('../portal/@slide-panel-preview/calendar-event-edit/calendar-event-edit.component').then(m => m.CalendarEventEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/calendar-event-edit/calendar-event-edit.module').then(m => m.CalendarEventEditModule),
    });
    static readonly SCHEDULER_EVENT_EDIT = new ContentDefinition({
        key: 'SCHEDULER_EVENT_EDIT',
        name: {lowerCase: 'scheduler event', capitalized: 'Scheduler Event'},
        icon: 'calendar-alt',
        loadComponent: () => import('../portal/@slide-panel-preview/scheduler-event-edit/scheduler-event-edit.component').then(m => m.SchedulerEventEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/scheduler-event-edit/scheduler-event-edit.module').then(m => m.SchedulerEventEditModule),
    });
    static readonly HL7_MESSAGE = new ContentDefinition({
        key: 'HL7_MESSAGE',
        name: {lowerCase: 'hl7 message', capitalized: 'HL7 Message'},
        path: 'system-maintenance/hl7/:id',
        modelBase: Hl7Message,
        loadComponent: () => import('../portal/@slide-panel-preview/hl7-message-details/hl7-message-details.component').then(m => m.Hl7MessageDetailsComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/hl7-message-details/hl7-message-details.module').then(m => m.Hl7MessageDetailsModule),
    });
    static readonly HL7_MESSAGES = new ContentDefinition({
        key: 'HL7_MESSAGES',
        name: {lowerCase: 'hl7 messages', capitalized: 'HL7 Messages'},
        loadComponent: () => import('../portal/system-maintenance/hl7-messages-container/hl7-messages/hl7-messages.component').then(m => m.Hl7MessagesComponent),
        loadChildren: () => import('../portal/system-maintenance/hl7-messages-container/hl7-messages/hl7-messages.module').then(m => m.Hl7MessagesModule),
    });
    static readonly SYNC_DETAIL = new ContentDefinition({
        key: 'SYNC_DETAIL',
        path: 'system-maintenance/import-errors/:syncProcessId',
        loadComponent: () => import('../portal/@slide-panel-preview/sync-detail/sync-detail.component').then(m => m.SyncDetailComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/sync-detail/sync-detail.module').then(m => m.SyncDetailModule),
    });
    static readonly SYNC_DETAIL_BY_FACILITY = new ContentDefinition({
        ...this.SYNC_DETAIL,
        path: 'system-maintenance/import-errors/:syncProcessId/:facilityId',
        pathStringParams: ['syncProcessId', 'facilityId'],
    });
    static readonly SYNC_PROCESS = new ContentDefinition({
        key: 'SYNC_PROCESS',
        path: 'system-maintenance/import-errors/syncProcess/:syncDetailId/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/sync-process/sync-process.component').then(m => m.SyncProcessComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/sync-process/sync-process.module').then(m => m.SyncProcessModule),
    });
    static readonly SYNC_DETAIL_TASK = new ContentDefinition({
        key: 'SYNC_DETAIL_TASK',
        path: 'system-maintenance/import-errors/:syncDetailId/:syncProcessId/:id',
        pathStringParams: ['id'],
        loadComponent: () => import('../portal/@slide-panel-preview/sync-task/sync-task.component').then(m => m.SyncTaskComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/sync-task/sync-task.module').then(m => m.SyncTaskModule),
    });
    static readonly USER = new ContentDefinition({
        key: 'USER',
        name: {lowerCase: 'user', capitalized: 'User'},
        icon: 'user',
        path: 'admin/users/:id',
    });
    static readonly USER_EDIT = new ContentDefinition({
        key: 'USER_EDIT',
        name: {lowerCase: 'user edit', capitalized: 'User Edit'},
        permissions: ['PERMISSION_WRITE_USER'],
        icon: this.USER.icon,
        path: 'admin/users/:id/edit',
    });
    static readonly USER_EDIT_NEW = new ContentDefinition({
        ...this.USER_EDIT,
        key: 'USER_EDIT_NEW',
        path: 'admin/users/new',
    });
    static readonly MEDICATION_INTERACTION = new ContentDefinition({
        key: 'MEDICATION_INTERACTION',
        name: {lowerCase: 'medication interaction', capitalized: 'Medication Interaction'},
    });
    static readonly EMR_UPLOAD_DETAIL = new ContentDefinition({
        key: 'EMR_UPLOAD_DETAIL',
        path: 'system-maintenance/emr-uploads/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/emr-upload-details/emr-upload-details.component').then(m => m.EmrUploadDetailsComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/emr-upload-details/emr-upload-details.module').then(m => m.EmrUploadDetailsModule),
    });
    static readonly EMR_UPLOADS = new ContentDefinition({
        key: 'EMR_UPLOADS',
        loadComponent: () => import('../portal/system-maintenance/emr-uploads/emr-uploads.component').then(m => m.EmrUploadsComponent),
        loadChildren: () => import('../portal/system-maintenance/emr-uploads/emr-uploads.module').then(m => m.EmrUploadsModule),
    });
    static readonly CDS_INTERVENTION = new ContentDefinition({
        key: 'CDS_INTERVENTION',
        path: 'system-settings/cds-interventions/:id',
        icon: 'comment-exclamation',
        loadComponent: () => import('../portal/admin/cds-interventions/cds-intervention/cds-intervention-details/cds-intervention-details.component').then(m => m.CdsInterventionDetailsComponent),
        loadChildren: () => import('../portal/admin/cds-interventions/cds-intervention/cds-intervention-details/cds-intervention-details.module').then(m => m.CdsInterventionDetailsModule),
    });
    static readonly PATIENT_DOCUMENT = new ContentDefinition({
        key: 'PATIENT_DOCUMENT',
        icon: 'file',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-document-uploader/patient-document-uploader.component').then(m => m.PatientDocumentUploaderComponent),
    });
    static readonly ORGANIZATION_EDIT = new ContentDefinition({
        key: 'ORGANIZATION_EDIT',
        name: {lowerCase: 'organization edit', capitalized: 'Organization Edit'},
        icon: 'building',
        path: 'system-settings/organizations/:id',
        loadComponent: () => import('../portal/admin/organizations/organization-edit/organization-edit.component').then(m => m.OrganizationEditComponent),
        loadChildren: () => import('../portal/admin/organizations/organization-edit/organization-edit.module').then(m => m.OrganizationEditModule),
        permissions: ['PERMISSION_WRITE_ORGANIZATION'],
    });
    static readonly FACILITY_EDIT = new ContentDefinition({
        key: 'FACILITY_EDIT',
        name: {lowerCase: 'facility edit', capitalized: 'Facility Edit'},
        icon: 'building',
        path: 'system-settings/organizations/facilities/:id/edit',
        loadComponent: () => import('../portal/admin/organizations/facility-edit/facility-edit.component').then(m => m.FacilityEditComponent),
        loadChildren: () => import('../portal/admin/organizations/facility-edit/facility-edit.module').then(m => m.FacilityEditModule),
        permissions: ['PERMISSION_WRITE_FACILITY'],
    });
    static readonly FACILITY_DETAILS = new ContentDefinition({
        key: 'FACILITY_DETAILS',
        name: {lowerCase: 'facility details', capitalized: 'Facility Details'},
        icon: 'building',
        path: 'system-settings/organizations/facilities/:id',
        loadComponent: () => import('../portal/admin/organizations/facility-details/facility-details.component').then(m => m.FacilityDetailsComponent),
        loadChildren: () => import('../portal/admin/organizations/facility-details/facility-details.module').then(m => m.FacilityDetailsModule),
        permissions: ['PERMISSION_WRITE_FACILITY'],
    });
    static readonly LABEL_EDIT = new ContentDefinition({
        key: 'LABEL_EDIT',
        path: 'system-settings/label/:id',
        loadComponent: () => import('../portal/admin/labels-manager/label-edit/label-edit.component').then(m => m.LabelEditComponent),
        loadChildren: () => import('../portal/admin/labels-manager/label-edit/label-edit.module').then(m => m.LabelEditModule),
        permissions: ['PERMISSION_WRITE_LABELS'],
    });
    static readonly LABEL_EDIT_NEW = new ContentDefinition({
        ...this.LABEL_EDIT,
        key: 'LABEL_EDIT_NEW',
        path: 'system-settings/label/new',
    });
    static readonly CLINICAL_NOTE = new ContentDefinition({
        key: 'CLINICAL_NOTE',
        path: 'clinical-notes/:id',
        loadChildren: () => import('../portal/@slide-panel-preview/clinical-note/clinical-notes.module').then(m => m.ClinicalNotesModule),
        loadComponent: () => import('../portal/@slide-panel-preview/clinical-note/clinical-note.component').then(m => m.ClinicalNoteComponent),
    });
    static readonly CLAIM = new ContentDefinition({
        key: 'CLAIM',
        icon: 'file-invoice-dollar',
        path: 'billing/claims/:id',
        modelBase: Claim,
    });
    static readonly REMITTANCE = new ContentDefinition({
        key: 'REMITTANCE',
        icon: 'file-invoice',
        path: 'billing/remittances/:id',
        modelBase: Remittance,
    });
    static readonly WOUND = new ContentDefinition({
        key: 'WOUND',
        modelBase: Wound,
        patientDetailsSubPath: '?tab=wounds',
        actionEntryKey: 'wound',
    });
}

export class SlidePanelDefinitions {
    static readonly PATIENT = new SlidePanelDefinition(ContentDefinitions.PATIENT, {
        size: 'lg',
        verticalAlign: 'top',
        showFab: true,
    });
    static readonly LAB_REPORT = new SlidePanelDefinition(ContentDefinitions.LAB_REPORT, {
        size: 'lg',
        showFab: true,
    });
    static readonly LAB_REPORT_HISTORY = new SlidePanelDefinition({
        key: 'LAB_REPORT_HISTORY',
        path: 'care-surveillance/lab-reports/:id/history',
        loadComponent: () => import('../portal/@slide-panel-preview/lab-report/lab-report-history/lab-report-history.component').then(m => m.LabReportHistoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-report/lab-report-history/lab-report-history.module').then(m => m.LabReportHistoryModule),
    });
    static readonly LAB_TEST = new SlidePanelDefinition(ContentDefinitions.LAB_TEST, {
        defRootUrl: 'care-surveillance/lab-reports',
        size: 'lg',
        showFab: true,
    });
    static readonly IMAGING_RESULT = new SlidePanelDefinition(ContentDefinitions.IMAGING_RESULT, {
        size: 'lg',
        showFab: true,
    });
    static readonly IMAGING_RESULT_EDIT = new SlidePanelDefinition({
        key: 'IMAGING_RESULT_EDIT',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@portal-shared/imaging/imaging-results-edit/imaging-results-edit-panel/imaging-results-edit-panel.component').then(m => m.ImagingResultsEditPanelComponent),
        loadChildren: () => import('../portal/@portal-shared/imaging/imaging-results-edit/imaging-results-edit-panel/imaging-results-edit-panel.module').then(m => m.ImagingResultsEditPanelModule),
    });
    static readonly IMAGING_ORDER = new SlidePanelDefinition({
        key: 'IMAGING_ORDER',
        path: 'patients/:patientId/imaging-orders/:imagingOrderId',
        pathStringParams: ['imagingOrderId'],
        defRootUrl: 'care-surveillance/imaging-results',
        size: 'lg',
        loadComponent: () => import('../portal/@slide-panel-preview/imaging-order/imaging-order.component').then(m => m.ImagingOrderComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/imaging-order/imaging-order.module').then(m => m.ImagingOrderModule),
    });
    static readonly CLINICAL_RESULT_EDIT = new SlidePanelDefinition({
        key: 'CLINICAL_RESULT_EDIT',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@portal-shared/clinical-results/clinical-results-edit/clinical-results-edit-panel/clinical-results-edit-panel.component').then(m => m.ClinicalResultsEditPanelComponent),
        loadChildren: () => import('../portal/@portal-shared/clinical-results/clinical-results-edit/clinical-results-edit-panel/clinical-results-edit-panel.module').then(m => m.ClinicalResultsEditPanelModule),
    });
    static readonly EVENT = new SlidePanelDefinition(ContentDefinitions.EVENT, {
        defRootUrl: 'patients',
        showFab: true,
    });
    static readonly VITAL = new SlidePanelDefinition(ContentDefinitions.VITAL, {
        defRootUrl: 'patients',
        showFab: true,
    });
    static readonly CRITICAL_VITALS = new SlidePanelDefinition(ContentDefinitions.CRITICAL_VITALS, {
        showFab: true,
    });
    static readonly MEDICATION = new SlidePanelDefinition(ContentDefinitions.MEDICATION, {
        verticalAlign: 'top',
        showFab: true,
    });
    static readonly PATIENT_INGREDIENT = new SlidePanelDefinition({
        key: 'PATIENT_INGREDIENT',
        path: 'patients/:patientId/ingredient/:id',
        icon: 'prescription-bottle',
        permissions: ['PERMISSION_READ_MEDICATION_LIST'],
        defRootUrl: 'patients',
        size: 'lg',
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-ingredient/patient-ingredient.component').then(m => m.PatientIngredientComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-ingredient/patient-ingredient.module').then(m => m.PatientIngredientModule),
    });
    static readonly PATIENT_DRUG_CATEGORY = new SlidePanelDefinition({
        key: 'PATIENT_DRUG_CATEGORY',
        path: 'patients/:patientId/drug-category/:id',
        icon: 'prescription-bottle',
        permissions: ['PERMISSION_READ_MEDICATION_LIST'],
        defRootUrl: 'patients',
        size: 'lg',
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-drug-category/patient-drug-category.component').then(m => m.PatientDrugCategoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-drug-category/patient-drug-category.module').then(m => m.PatientDrugCategoryModule),
    });
    static readonly PATIENT_ICD = new SlidePanelDefinition(ContentDefinitions.PATIENT_ICD, {
        defRootUrl: 'patients',
        size: 'lg',
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-icd/patient-icd.component').then(m => m.PatientIcdComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-icd/patient-icd.module').then(m => m.PatientIcdModule),
    });
    static readonly PATIENT_SNOMED = new SlidePanelDefinition(ContentDefinitions.PATIENT_SNOMED, {
        defRootUrl: 'patients',
        size: 'lg',
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-snomed/patient-snomed.component').then(m => m.PatientSnomedComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-snomed/patient-snomed.module').then(m => m.PatientSnomedModule),
    });
    static readonly PATIENT_ICD_GROUP = new SlidePanelDefinition({
        key: 'PATIENT_ICD_GROUP',
        path: 'patients/:patientId/icd-category/:id',
        icon: 'stethoscope',
        permissions: ['PERMISSION_READ_MEDICATION_LIST'],
        defRootUrl: 'patients',
        size: 'lg',
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-icd-group/patient-icd-group.component').then(m => m.PatientIcdGroupComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-icd-group/patient-icd-group.module').then(m => m.PatientIcdGroupModule),
    });
    static readonly IMMUNIZATION_LIST = new SlidePanelDefinition({
        key: 'IMMUNIZATION_LIST',
        path: 'patients/:id/immunizations',
        verticalAlign: 'top',
        customMarkup: true,
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/immunizations/immunization-list/immunization-list-panel/immunization-list-panel.component').then(m => m.ImmunizationListPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/immunizations/immunization-list/immunization-list-panel/immunization-list-panel.module').then(m => m.ImmunizationListPanelModule),
    });
    static readonly IMMUNIZATION_EDIT = new SlidePanelDefinition({
        key: 'IMMUNIZATION_EDIT',
        permissions: ['PERMISSION_WRITE_PATIENT_CLINICAL'],
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/immunizations/immunization-edit/immunization-edit.component').then(m => m.ImmunizationEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/immunizations/immunization-edit/immunization-edit.module').then(m => m.ImmunizationEditModule),
    });
    static readonly PROCEDURE_LIST = new SlidePanelDefinition({
        key: 'PROCEDURE_LIST',
        path: 'patients/:id/procedures',
        verticalAlign: 'top',
        customMarkup: true,
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/procedures/procedure-list/procedure-list-panel/procedure-list-panel.component').then(m => m.ProcedureListPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/procedures/procedure-list/procedure-list-panel/procedure-list-panel.module').then(m => m.ProcedureListPanelModule),
    });
    static readonly PROCEDURE_EDIT = new SlidePanelDefinition({
        key: 'PROCEDURE_EDIT',
        permissions: ['PERMISSION_WRITE_PATIENT_CLINICAL'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/procedures/procedure-edit/procedure-edit.component').then(m => m.ProcedureEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/procedures/procedure-edit/procedure-edit.module').then(m => m.ProcedureEditModule),
    });
    static readonly ALLERGY_LIST = new SlidePanelDefinition({
        key: 'ALLERGY_LIST',
        path: 'patients/:id/allergies',
        verticalAlign: 'top',
        customMarkup: true,
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/allergies/allergy-list/allergy-list-panel/allergy-list-panel.component').then(m => m.AllergyListPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/allergies/allergy-list/allergy-list-panel/allergy-list-panel.module').then(m => m.AllergyListPanelModule),
    });
    static readonly ALLERGY_EDIT = new SlidePanelDefinition({
        key: 'ALLERGY_EDIT',
        permissions: ['PERMISSION_WRITE_PATIENT_CLINICAL'],
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/allergies/allergy-edit/allergy-edit.component').then(m => m.AllergyEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/allergies/allergy-edit/allergy-edit.module').then(m => m.AllergyEditModule),
    });
    static readonly TASK = new SlidePanelDefinition(ContentDefinitions.TASK);
    static readonly TASK_HISTORY = new SlidePanelDefinition({
        key: 'TASK_HISTORY',
        path: 'arsana-notes/:id/history',
        loadComponent: () => import('../portal/@slide-panel-preview/ccm-note/note-editing-history/note-editing-history.component').then(m => m.NoteEditingHistoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/ccm-note/note-editing-history/note-editing-history.module').then(m => m.NoteEditingHistoryModule),
    });
    static readonly TASK_PROPOSAL = new SlidePanelDefinition(ContentDefinitions.TASK_PROPOSAL, {
        defRootUrl: 'patients',
    });
    static readonly TASK_PROPOSAL_EDIT = new SlidePanelDefinition({
        key: 'TASK_PROPOSAL_EDIT',
        name: this.TASK_PROPOSAL.name,
        icon: this.TASK_PROPOSAL.icon,
        path: 'task-proposal/:id/edit',
        defRootUrl: 'patients',
        loadComponent: () => import('../portal/@slide-panel-preview/task-proposal-edit/task-proposal-edit.component').then(m => m.TaskProposalEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/task-proposal-edit/task-proposal-edit.module').then(m => m.TaskProposalEditModule),
    });
    static readonly TASK_PROPOSAL_NEW = new SlidePanelDefinition({
        key: 'TASK_PROPOSAL_NEW',
        name: this.TASK_PROPOSAL_EDIT.name,
        icon: this.TASK_PROPOSAL_EDIT.icon,
        path: 'task-proposal/new/:patient',
        loadComponent: this.TASK_PROPOSAL_EDIT.loadComponent,
        loadChildren: this.TASK_PROPOSAL_EDIT.loadChildren,
        defRootUrl: 'patients',
    });
    static readonly VISIT_EDIT = new SlidePanelDefinition({
        key: 'VISIT_EDIT',
        name: {lowerCase: 'visit occasion', capitalized: 'Visit Occasion'},
        icon: 'clipboard-list-check',
        size: 'lg',
        loadComponent: () => import('../portal/@slide-panel-preview/visit-edit/visit-edit.component').then(m => m.VisitEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/visit-edit/visit-edit.module').then(m => m.VisitEditModule),
    });
    static readonly VISIT_COMPOSING_PATIENT_LIST = new SlidePanelDefinition({
        key: 'VISIT_COMPOSING_PATIENT_LIST',
        name: {lowerCase: 'patient list', capitalized: 'Patient List'},
        icon: 'clipboard-user',
        size: 'lg',
        loadComponent: () => import('../portal/@slide-panel-preview/visit-composing-patient-list/visit-composing-patient-list.component').then(m => m.VisitComposingPatientListComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/visit-composing-patient-list/visit-composing-patient-list.module').then(m => m.VisitComposingPatientListModule),
    });
    static readonly VISIT_COMPOSING_BY_OCCASIONS = new SlidePanelDefinition({
        key: 'VISIT_COMPOSING_BY_OCCASIONS',
        name: {lowerCase: 'patient list', capitalized: 'Patient List'},
        icon: 'clipboard-user',
        size: 'sm',
        loadComponent: () => import('../portal/visit/visit-composing-by-occasions/visit-composing-by-occasions.component').then(m => m.VisitComposingByOccasionsComponent),
        loadChildren: () => import('../portal/visit/visit-composing-by-occasions/visit-composing-by-occasions.module').then(m => m.VisitComposingByOccasionsModule),
    });
    static readonly PATIENT_VITAL_HISTORY = new SlidePanelDefinition({
        key: 'PATIENT_VITAL_HISTORY',
        name: {lowerCase: 'vital history', capitalized: 'Vital History'},
        icon: 'heartbeat',
        defRootUrl: 'patients',
        size: 'lg',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-vitals-history/patient-vitals-history.component').then(m => m.PatientVitalsHistoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-vitals-history/patient-vitals-history.module').then(m => m.PatientVitalsHistoryModule),
    });
    static readonly LAB_SHIPMENT_CREATE = new SlidePanelDefinition({
        key: 'LAB_SHIPMENT_CREATE',
        name: {lowerCase: 'new testing order', capitalized: 'New Testing Order'},
        icon: 'dolly',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/lab-shipment-create-panel/lab-shipment-create-panel.component').then(m => m.LabShipmentCreatePanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/lab-shipment-create-panel/lab-shipment-create-panel.module').then(m => m.LabShipmentCreatePanelModule),
    });
    static readonly COVID_SHIPMENT_CREATE = new SlidePanelDefinition({
        key: 'COVID_SHIPMENT_CREATE',
        name: {lowerCase: 'new COVID testing order', capitalized: 'New COVID Testing Order'},
        icon: 'dolly',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/covid-shipment-create-panel/covid-shipment-create-panel.component').then(m => m.CovidShipmentCreatePanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/covid-shipment-create-panel/covid-shipment-create-panel.module').then(m => m.CovidShipmentCreatePanelModule),
    });
    static readonly LAB_SHIPMENT = new SlidePanelDefinition(ContentDefinitions.LAB_SHIPMENT, {
        verticalAlign: 'top',
        customMarkup: true,
    });
    static readonly LAB_SHIPMENT_BOX = new SlidePanelDefinition(ContentDefinitions.LAB_SHIPMENT_BOX, {
        defRootUrl: 'covid-testing/shipments',
        verticalAlign: 'top',
        customMarkup: true,
    });
    static readonly LAB_SHIPMENT_BAG = new SlidePanelDefinition(ContentDefinitions.LAB_SHIPMENT_BAG, {
        defRootUrl: 'covid-testing/shipments',
        verticalAlign: 'top',
        customMarkup: true,
    });
    static readonly LAB_SHIPMENT_ORDER = new SlidePanelDefinition(ContentDefinitions.LAB_SHIPMENT_ORDER, {
        defRootUrl: 'covid-testing/shipments',
        customMarkup: true,
    });
    static readonly PERSON_EDIT = new SlidePanelDefinition(ContentDefinitions.PERSON_EDIT, {
        verticalAlign: 'top',
        defRootUrl: 'patients',
        loadComponent: () => import('../portal/@slide-panel-preview/person-edit/person-edit.component').then(m => m.PersonEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/person-edit/person-edit.module').then(m => m.PersonEditModule),
    });
    static readonly PATIENT_MERGE = new SlidePanelDefinition({
        key: 'PATIENT_MERGE',
        name: {lowerCase: 'patient merge', capitalized: 'Patient Merge'},
        icon: 'pen-alt',
        path: 'patients/merge/:id',
        defRootUrl: 'patients',
        size: 'lg',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-merge/patient-merge.component').then(m => m.PatientMergeComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-merge/patient-merge.module').then(m => m.PatientMerge),
    });
    static readonly PATIENT_CMRN_LIST = new SlidePanelDefinition({
        key: 'PATIENT_CMRN_LIST',
        name: {lowerCase: 'patient cmrn list', capitalized: 'Patients\' CMRN list'},
        icon: 'pen-alt',
        size: 'lg',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-cmrn-list/patient-cmrn-list.component').then(m => m.PatientCmrnListComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-cmrn-list/patient-cmrn-list.module').then(m => m.PatientCmrnListModule),
    });
    static readonly PATIENT_EDIT_NEW = new SlidePanelDefinition({
        key: 'PATIENT_EDIT_NEW',
        name: {lowerCase: 'new patient', capitalized: 'New Patient'},
        icon: 'address-card',
        path: 'admin/patients/new',
        permissions: ['PERMISSION_WRITE_PATIENTS'],
        defRootUrl: 'patients',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-edit/patient-edit.component').then(m => m.PatientEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-edit/patient-edit.module').then(m => m.PatientEditModule),
    });
    static readonly PATIENT_INTAKE = new SlidePanelDefinition({
        key: 'PATIENT_INTAKE',
        name: {lowerCase: 'patient intake', capitalized: 'Patient Intake'},
        icon: 'address-card',
        path: 'patients/:id/intake',
        permissions: ['PERMISSION_WRITE_PATIENTS'],
        defRootUrl: 'patients',
        customMarkup: true,
        loadComponent: () => import('../portal/bottom-panel/patient-intake/patient-intake.component').then(m => m.PatientIntakeComponent),
        loadChildren: () => import('../portal/bottom-panel/patient-intake/patient-intake.module').then(m => m.PatientIntakeModule),
    });
    static readonly PENDING_INTAKES = new SlidePanelDefinition({
        key: 'PENDING_INTAKES',
        name: {lowerCase: 'pending intakes', capitalized: 'Pending Intakes'},
        icon: 'address-card',
        path: 'patients/pending-intakes',
        permissions: ['PERMISSION_WRITE_PATIENTS'],
        customMarkup: true,
        size: 'lg',
        loadComponent: () => import('../portal/dashboard/census-oversight/pending-intakes-list/pending-intakes-list.component').then(m => m.PendingIntakesListComponent),
        loadChildren: () => import('../portal/dashboard/census-oversight/pending-intakes-list/pending-intakes-list.module').then(m => m.PendingIntakesListModule),
    });
    static readonly BILLING_GROUP_PLAN_EDIT = new SlidePanelDefinition({
        key: 'CUSTOMER_CONTRACT_EDIT',
        path: 'billing/insurance-plan-management/:id',
        permissions: ['PERMISSION_READ_BILLING_GROUP_PLAN', 'PERMISSION_WRITE_BILLING_GROUP_PLAN'],
        customMarkup: true,
        loadComponent: () => import('../portal/admin/insurance-plan-management/billing-group-plan-edit/billing-group-plan-edit.component').then(m => m.BillingGroupPlanEditComponent),
        loadChildren: () => import('../portal/admin/insurance-plan-management/billing-group-plan-edit/billing-group-plan-edit.module').then(m => m.BillingGroupPlanEditModule),
    });
    static readonly BILLING_GROUP_PLAN_NEW = new SlidePanelDefinition(
        {
            ...SlidePanelDefinitions.BILLING_GROUP_PLAN_EDIT,
            permissions: ['PERMISSION_WRITE_BILLING_GROUP_PLAN'],
            key: 'CUSTOMER_CONTRACT_NEW',
            path: 'billing/insurance-plan-management/new',
        }
    );
    static readonly IMPORT_ACCOUNT = new SlidePanelDefinition(ContentDefinitions.IMPORT_ACCOUNT, {
        verticalAlign: 'top',
        size: 'md',
    });
    static readonly IMPORT_ACCOUNT_NEW = new SlidePanelDefinition(ContentDefinitions.IMPORT_ACCOUNT_NEW, {
        verticalAlign: 'top',
        size: 'md',
    });
    static readonly IMPORT_MANAGER = new SlidePanelDefinition(ContentDefinitions.IMPORT_MANAGER, {
        defRootUrl: 'admin/import-accounts',
        verticalAlign: 'top',
        size: 'lg',
    });
    static readonly CALENDAR_EVENT = new SlidePanelDefinition(ContentDefinitions.CALENDAR_EVENT, {
        size: 'sm',
    });
    static readonly SCHEDULER_EVENT_EDIT = new SlidePanelDefinition(ContentDefinitions.SCHEDULER_EVENT_EDIT, {
        size: 'sm',
        customMarkup: true,
    });
    static readonly SCHEDULER_OCCURRENCE_HISTORY = new SlidePanelDefinition({
        key: 'SCHEDULER_OCCURRENCE_HISTORY',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/scheduler-occurrence-history/scheduler-occurrence-history.component').then(m => m.SchedulerOccurrenceHistoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/scheduler-occurrence-history/scheduler-occurrence-history.module').then(m => m.SchedulerOccurrenceHistoryModule),
    });
    static readonly TIME_LOGS = new SlidePanelDefinition({
        key: 'TIME_LOGS',
        permissions: ['PERMISSION_READ_TIME_LOG'],
        customMarkup: true,
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/time-logs-list/time-logs-list.component').then(m => m.TimeLogsListComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/time-logs-list/time-logs-list.module').then(m => m.TimeLogsListModule),
    });
    static readonly HL7_MESSAGE = new SlidePanelDefinition(ContentDefinitions.HL7_MESSAGE, {
        size: 'lg',
    });
    static readonly HL7_MESSAGES = new SlidePanelDefinition(ContentDefinitions.HL7_MESSAGES, {
        size: 'lg',
        customMarkup: true,
    });
    static readonly ORGANISM = new SlidePanelDefinition({
        key: 'ORGANISM',
        icon: 'bacteria',
        path: 'care-surveillance/culture-specimen-dashboard/:id',
        loadComponent: () => import('../portal/@slide-panel-preview/organism-panel/organism-panel.component').then(m => m.OrganismPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/organism-panel/organism-panel.module').then(m => m.OrganismPanelModule),
    });
    static readonly SYNC_DETAIL = new SlidePanelDefinition(ContentDefinitions.SYNC_DETAIL, {
        verticalAlign: 'top',
        defRootUrl: 'system-maintenance/import-errors?tab=syncProcesses',
    });
    static readonly SYNC_DETAIL_BY_FACILITY = new SlidePanelDefinition(ContentDefinitions.SYNC_DETAIL_BY_FACILITY, {
        verticalAlign: 'top',
    });
    static readonly SYNC_PROCESS = new SlidePanelDefinition(ContentDefinitions.SYNC_PROCESS, {
        verticalAlign: 'top',
    });
    static readonly SYNC_DETAIL_TASK = new SlidePanelDefinition(ContentDefinitions.SYNC_DETAIL_TASK, {
        verticalAlign: 'top',
    });
    static readonly EMBED = new SlidePanelDefinition(new ContentDefinition({key: 'EMBED'}), {
        customMarkup: true,
        loadComponent: () => import('../portal/@portal-theme/components/embed/embed.component').then(m => m.EmbedComponent),
    });
    static readonly USER = new SlidePanelDefinition(ContentDefinitions.USER, {
        size: 'lg',
        loadComponent: () => import('../portal/admin/users/user-details/user-details.component').then(m => m.UserDetailsComponent),
        loadChildren: () => import('../portal/admin/users/user-details/user-details.module').then(m => m.UserDetailsModule),
    });
    static readonly USER_EDIT = new SlidePanelDefinition(ContentDefinitions.USER_EDIT, {
        defRootUrl: 'admin/users',
        size: 'lg',
        loadComponent: () => import('../portal/@slide-panel-preview/user-edit/user-edit.component').then(m => m.UserEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/user-edit/user-edit.module').then(m => m.UserEditModule),
    });
    static readonly USER_EDIT_NEW = new SlidePanelDefinition(ContentDefinitions.USER_EDIT_NEW, {
        defRootUrl: this.USER_EDIT.defRootUrl,
        size: this.USER_EDIT.size,
        loadComponent: this.USER_EDIT.loadComponent,
        loadChildren: this.USER_EDIT.loadChildren,
    });
    static readonly EMR_UPLOAD_DETAIL = new SlidePanelDefinition(ContentDefinitions.EMR_UPLOAD_DETAIL, {
        size: 'lg',
        customMarkup: true,
    });
    static readonly EMR_UPLOADS = new SlidePanelDefinition(ContentDefinitions.EMR_UPLOADS, {
        size: 'lg',
        customMarkup: true,
    });
    static readonly PATIENT_ACCESS_SUMMARY = new SlidePanelDefinition({
        key: 'PATIENT_ACCESS_SUMMARY',
        path: 'admin/patient-access-management/patient/:patientId',
        defRootUrl: 'admin/patient-access-management',
        size: 'lg',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-access-summary/patient-access-summary.component').then(m => m.PatientAccessSummaryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-access-summary/patient-access-summary.module').then(m => m.PatientAccessSummaryModule),
    });
    static readonly MEDICAL_PROGRAM_HISTORY = new SlidePanelDefinition({
        key: 'MEDICAL_PROGRAMS',
        loadComponent: () => import('../portal/@slide-panel-preview/medical-program-history/medical-program-history.component').then(m => m.MedicalProgramHistoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/medical-program-history/medical-program-history.module').then(m => m.MedicalProgramHistoryModule),
    });
    static readonly APP_CREATE = new SlidePanelDefinition({
        key: 'APP_CREATE',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/admin/app-management/app-create/app-create.component').then(m => m.AppCreateComponent),
        loadChildren: () => import('../portal/admin/app-management/app-management.module').then(m => m.AppManagementModule),
    });
    static readonly PATIENT_ACCESS_HISTORY = new SlidePanelDefinition({
        key: 'PATIENT_ACCESS_HISTORY',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-access-history/patient-access-history.component').then(m => m.PatientAccessHistoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-access-history/patient-access-history.module').then(m => m.PatientAccessHistoryModule),
    });
    static readonly PATIENT_SPECIALTY_SUMMARY = new SlidePanelDefinition({
        key: 'PATIENT_SPECIALTY_SUMMARY',
        path: 'patients/:patientId/specialty/:specialtyId',
        defRootUrl: '/patients',
        size: 'lg',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-specialty-summary-details/patient-specialty-summary-details.component').then(m => m.PatientSpecialtySummaryDetailsComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-specialty-summary-details/patient-specialty-summary-details.module').then(m => m.PatientSpecialtySummaryDetailsModule),
    });
    static readonly CENSUS_EDIT = new SlidePanelDefinition({
        key: 'CENSUS_EDIT',
        permissions: ['PERMISSION_WRITE_CENSUS_HISTORY', 'PERMISSION_WRITE_PATIENT_ADMIN', 'PERMISSION_WRITE_PATIENT_CLINICAL'],
        path: 'patients/:patientId/census-edit/:id',
        defRootUrl: '/patients',
        size: 'lg',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@portal-shared/census-edit/census-edit.component').then(m => m.CensusEditComponent),
        loadChildren: () => import('../portal/@portal-shared/census-edit/census-edit.module').then(m => m.CensusEditModule),
    });
    static readonly HOSPITAL_LEAVES_PANEL = new SlidePanelDefinition({
        key: 'HOSPITAL_LEAVES_PANEL',
        permissions: ['PERMISSION_WRITE_CENSUS_HISTORY', 'PERMISSION_WRITE_PATIENT_ADMIN', 'PERMISSION_WRITE_PATIENT_CLINICAL'],
        path: 'patients/:id/hospital-leaves',
        size: 'lg',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/hospital-leaves/hospital-leaves-panel/hospital-leaves-panel.component').then(m => m.HospitalLeavesPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/hospital-leaves/hospital-leaves-panel/hospital-leaves-panel.module').then(m => m.HospitalLeavesPanelModule),
    });
    static readonly CENSUS_EDIT_NEW = new SlidePanelDefinition(SlidePanelDefinitions.CENSUS_EDIT, {
        key: 'CENSUS_EDIT_NEW',
        path: 'patients/:patientId/census-edit/new',
        verticalAlign: 'top',
    });
    static readonly CENSUS_EDIT_HISTORY = new SlidePanelDefinition({
        key: 'CENSUS_EDIT_HISTORY',
        permissions: ['PERMISSION_WRITE_CENSUS_HISTORY'],
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@portal-shared/census-edit-history/census-edit-history.component').then(m => m.CensusEditHistoryComponent),
        loadChildren: () => import('../portal/@portal-shared/census-edit-history/census-edit-history.module').then(m => m.CensusEditHistoryModule),
    });
    static readonly SYSTEM_ALERT_EDIT = new SlidePanelDefinition({
        key: 'SYSTEM_ALERT_EDIT',
        permissions: ['PERMISSION_WRITE_SYSTEM_ALERTS'],
        size: 'sm',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/admin/system-alerts/system-alert-edit/system-alert-edit.component').then(m => m.SystemAlertEditComponent),
        loadChildren: () => import('../portal/admin/system-alerts/system-alert-edit/system-alert-edit.module').then(m => m.SystemAlertEditModule),
    });
    static readonly DEVICE_LIST = new SlidePanelDefinition({
        key: 'DEVICE_LIST',
        path: 'patients/:id/devices',
        size: 'lg',
        verticalAlign: 'top',
        customMarkup: true,
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/devices/device-list/device-list-panel/device-list-panel.component').then(m => m.DeviceListPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/devices/device-list/device-list-panel/device-list-panel.module').then(m => m.DeviceListPanelModule),
    });
    static readonly RELATED_PERSON_EDIT = new SlidePanelDefinition({
        key: 'RELATED_PERSON_EDIT',
        permissions: ['PERMISSION_WRITE_PATIENT_CLINICAL'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/related-persons/related-person-edit/related-person-edit.component').then(m => m.RelatedPersonEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/related-persons/related-person-edit/related-person-edit.module').then(m => m.RelatedPersonEditModule),
    });
    static readonly PERSON_LINK = new SlidePanelDefinition({
        key: 'PERSON_LINK',
        permissions: ['PERMISSION_WRITE_PERSONS'],
        size: 'lg',
        loadComponent: () => import('../portal/admin/person-management/person-link/person-link.component').then(m => m.PersonLinkComponent),
        loadChildren: () => import('../portal/admin/person-management/person-link/person-link.module').then(m => m.PersonLinkModule),
    });
    static readonly RELATED_PERSON_LIST = new SlidePanelDefinition({
        key: 'RELATED_PERSON_LIST',
        path: 'patients/:id/related-persons',
        verticalAlign: 'top',
        customMarkup: true,
        showFab: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/related-persons/related-person-list/related-person-list-panel/related-person-list-panel.component').then(m => m.RelatedPersonListPanelComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/related-persons/related-person-list/related-person-list-panel/related-person-list-panel.module').then(m => m.RelatedPersonListPanelModule),
    });
    static readonly ADDITIONAL_NAME_EDIT_NEW = new SlidePanelDefinition({
        key: 'ADDITIONAL_NAME_EDIT_NEW',
        path: 'patients/:patientId/additional-names/new',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/additional-names/additional-name-edit/additional-name-edit.component').then(m => m.AdditionalNameEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/additional-names/additional-name-edit/additional-name-edit.module').then(m => m.AdditionalNameEditModule),
    });
    static readonly ADDITIONAL_NAME_EDIT = new SlidePanelDefinition({
        ...this.ADDITIONAL_NAME_EDIT_NEW,
        key: 'ADDITIONAL_NAME_EDIT',
        path: 'patients/:patientId/additional-names/:id',
    });
    static readonly ADDITIONAL_NAME_LIST = new SlidePanelDefinition({
        key: 'ADDITIONAL_NAME_LIST',
        path: 'patients/:patientId/additional-names',
        verticalAlign: 'top',
        customMarkup: true,
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/additional-names/additional-name-list/additional-name-list.module').then(m => m.AdditionalNameListModule),
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/additional-names/additional-name-list/additional-name-list.component').then(m => m.AdditionalNameListComponent),
    });
    static readonly PATIENT_EMAIL_EDIT = new SlidePanelDefinition({
        key: 'PATIENT_EMAIL_EDIT',
        // path: ??
        permissions: ['PERMISSION_WRITE_PATIENT_ADMIN'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-contact-info/patient-email/patient-email-edit/patient-email-edit.component').then(m => m.PatientEmailEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-contact-info/patient-email/patient-email-edit.module').then(m => m.PatientEmailEditModule),
    });
    static readonly PATIENT_PHONE_EDIT = new SlidePanelDefinition({
        key: 'PATIENT_PHONE_EDIT',
        // path: ??
        permissions: ['PERMISSION_WRITE_PATIENT_ADMIN'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-contact-info/patient-phone/patient-phone-edit/patient-phone-edit.component').then(m => m.PatientPhoneEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-contact-info/patient-phone/patient-phone-edit.module').then(m => m.PatientPhoneEditModule),
    });
    static readonly PATIENT_ADDRESS_EDIT = new SlidePanelDefinition({
        key: 'PATIENT_ADDRESS_EDIT',
        // path: ??
        permissions: ['PERMISSION_WRITE_PATIENT_ADMIN'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-contact-info/patient-address/patient-address-edit/patient-address-edit.component').then(m => m.PatientAddressEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-contact-info/patient-address/patient-address-edit.module').then(m => m.PatientAddressEditModule),
    });
    static readonly CDS_INTERVENTION = new SlidePanelDefinition(ContentDefinitions.CDS_INTERVENTION, {
        verticalAlign: 'top',
        showFab: true,
        customMarkup: true,
    });
    static readonly CDS_INTERVENTION_EDIT = new SlidePanelDefinition({
        key: 'CDS_INTERVENTION_EDIT',
        // path: 'admin/cds-interventions/:id/edit',
        permissions: ['PERMISSION_WRITE_CDS'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/admin/cds-interventions/cds-intervention/cds-intervention-edit/cds-intervention-edit.component').then(m => m.CdsInterventionEditComponent),
        loadChildren: () => import('../portal/admin/cds-interventions/cds-intervention/cds-intervention-edit/cds-intervention-edit.module').then(m => m.CdsInterventionEditModule),
    });
    static readonly CDS_INTERVENTION_CONDITION_EDIT = new SlidePanelDefinition({
        key: 'CDS_INTERVENTION_CONDITION_EDIT',
        permissions: ['PERMISSION_WRITE_CDS'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/admin/cds-interventions/cds-intervention-condition/cds-intervention-condition-edit/cds-intervention-condition-edit.component').then(m => m.CdsInterventionConditionEditComponent),
        loadChildren: () => import('../portal/admin/cds-interventions/cds-intervention-condition/cds-intervention-condition-edit/cds-intervention-condition-edit.module').then(m => m.CdsInterventionConditionEditModule),
    });
    static readonly REPRESENTATIVE_LIST = new SlidePanelDefinition({
        key: 'REPRESENTATIVE_LIST',
        permissions: ['PERMISSION_CREATE_USER_FROM_PATIENT'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/representatives/representatives-list/representatives-list.component').then(m => m.RepresentativesListComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/representatives/representatives-list/representative-list.module').then(m => m.RepresentativeListModule),
    });
    static readonly REPRESENTATIVE_EDIT = new SlidePanelDefinition({
        key: 'REPRESENTATIVE_EDIT',
        permissions: ['PERMISSION_CREATE_USER_FROM_PATIENT'],
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/representatives/representatives-edit/representatives-edit.component').then(m => m.RepresentativesEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/representatives/representatives-edit/representatives-edit.module').then(m => m.RepresentativesEditModule),
    });
    static readonly CDA_RECONCILE = new SlidePanelDefinition({
        key: 'CDA_RECONCILE',
        path: 'patient/:patientId/cda-reconcile/:id',
        size: 'lg',
        verticalAlign: 'top',
        defRootUrl: 'patients',
        loadComponent: () => import('../portal/@slide-panel-preview/cda-reconcile/cda-reconcile.component').then(m => m.CdaReconcileComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/cda-reconcile/cda-reconcile.module').then(m => m.CdaReconcileModule),
    });
    static readonly FAX_ACTIVATION = new SlidePanelDefinition({
        key: 'FAX_ACTIVATION',
        size: 'md',
        verticalAlign: 'center',
        permissions: ['PERMISSION_WRITE_FAX_NUMBER'],
        loadComponent: () => import('../portal/@slide-panel-preview/fax-token-activation/fax-token-activation.component').then(m => m.FaxTokenActivationComponent),
    });
    static readonly ORGANIZATION_EDIT = new SlidePanelDefinition(ContentDefinitions.ORGANIZATION_EDIT, {
        size: 'md',
        verticalAlign: 'top',
        defRootUrl: 'system-settings/organizations',
        customMarkup: true,
    });
    static readonly ORGANIZATION_EDIT_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.ORGANIZATION_EDIT,
        path: 'system-settings/organizations/new',
    });
    static readonly FACILITY_EDIT = new SlidePanelDefinition(ContentDefinitions.FACILITY_EDIT, {
        size: 'lg',
        verticalAlign: 'top',
        defRootUrl: 'system-settings/organizations',
        customMarkup: true,
    });
    static readonly FACILITY_DETAILS = new SlidePanelDefinition(ContentDefinitions.FACILITY_DETAILS, {
        size: 'lg',
        verticalAlign: 'top',
        defRootUrl: 'system-settings/organizations',
        customMarkup: true,
    });
    static readonly FACILITY_EDIT_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.FACILITY_EDIT,
        path: 'system-settings/organizations/facilities/new',
    });
    static readonly HL7_LAB = new SlidePanelDefinition({
        key: 'HL7_LAB',
        size: 'lg',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/system-maintenance/lab-dashboard/hl7-lab/hl7-lab.component').then(m => m.Hl7LabComponent),
        customMarkup: true,
    });
    static readonly PATIENT_DOCUMENT = new SlidePanelDefinition({
        key: 'PATIENT_DOCUMENT',
        size: 'md',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-document-uploader/patient-document-uploader.component').then(m => m.PatientDocumentUploaderComponent),
    });
    static readonly VISIT_BILLING_DAY = new SlidePanelDefinition({
        key: 'VISIT_BILLING_DAY',
        path: 'billing/visit-days/:date/:userId',
        pathStringParams: ['date'],
        defRootUrl: 'billing/visit-billing',
        size: 'lg',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/billing/visit-billing-per-day/visit-billing-day/visit-billing-day.component').then(m => m.VisitBillingDayComponent),
        loadChildren: () => import('../portal/billing/visit-billing-per-day/visit-billing-day/visit-billing-day.module').then(m => m.VisitBillingDayModule),
    });
    static readonly LABEL_EDIT = new SlidePanelDefinition(ContentDefinitions.LABEL_EDIT, {
        key: 'LABEL_EDIT',
        verticalAlign: 'top',
    });
    static readonly LABEL_EDIT_NEW = new SlidePanelDefinition(ContentDefinitions.LABEL_EDIT_NEW, {
        key: 'LABEL_EDIT_NEW',
        verticalAlign: 'top',
        // defRootUrl: 'admin/labels-manager',
    });
    static readonly BILLING_GROUP_EDIT = new SlidePanelDefinition({
        key: 'BILLING_GROUP_EDIT',
        path: 'system-settings/billing-groups/:id',
        loadComponent: () => import('../portal/admin/billing-groups/billing-group-edit/billing-group-edit.component').then(m => m.BillingGroupEditComponent),
        loadChildren: () => import('../portal/admin/billing-groups/billing-group-edit/billing-group-edit.module').then(m => m.BillingGroupEditModule),
        permissions: ['PERMISSION_WRITE_BILLING_GROUP'],
    });
    static readonly BILLING_GROUP_EDIT_NEW = new SlidePanelDefinition({
        ...this.BILLING_GROUP_EDIT,
        key: 'BILLING_GROUP_EDIT_NEW',
        path: 'system-settings/billing-groups/new',
    });
    static readonly CHARGE_AMOUNT_SETTINGS = new SlidePanelDefinition({
        key: 'CHARGE_AMOUNT_SETTINGS',
        path: 'admin/charge-amount-settings/:id',
        defRootUrl: 'system-settings/billing-groups',
        size: 'max',
        loadComponent: () => import('../portal/admin/charge-amount-settings/charge-amount-settings.component').then(m => m.ChargeAmountSettingsComponent),
        loadChildren: () => import('../portal/admin/charge-amount-settings/charge-amount-settings.module').then(m => m.ChargeAmountSettingsModule),
        permissions: ['PERMISSION_WRITE_CHARGE_AMOUNT_SETTINGS'],
    });
    static readonly CHARGE_AMOUNT_SETTINGS_NEW = new SlidePanelDefinition({
        ...this.CHARGE_AMOUNT_SETTINGS,
        key: 'CHARGE_AMOUNT_SETTINGS_NEW',
        path: 'system-settings/charge-amount-settings/new',
    });
    static readonly PSYCHOTROPICS_DETAIL = new SlidePanelDefinition({
        key: 'PSYCHOTROPICS_DETAIL',
        size: 'lg',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/care-surveillance/psychotropics/psychotropics-detail/psychotropics-detail.component').then(m => m.PsychotropicsDetailComponent),
        loadChildren: () => import('../portal/care-surveillance/psychotropics/psychotropics-detail/psychotropics-detail.module').then(m => m.PsychotropicsDetailModule),
    });
    static readonly ADVANCED_MONITORING_PATIENTS = new SlidePanelDefinition({
        key: 'ADVANCED_MONITORING_PATIENTS',
        size: 'lg',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/advanced-monitoring-patients/advanced-monitoring-patients.component').then(m => m.AdvancedMonitoringPatientsComponent),
    });
    static readonly INFECTION_ENTRY_LIST = new SlidePanelDefinition({
        key: 'INFECTION_ENTRY_LIST',
        size: 'lg',
        customMarkup: true,
        loadComponent: () => import('../portal/@portal-shared/infection-lists/infection-entry-paginated-panel/infection-entry-paginated-panel.component').then(m => m.InfectionEntryPaginatedPanelComponent),
    });
    // Deliberately pathless: Arsana Views is scoped by the sidebar picker, not
    // by a facility in its URL, so there is no per-facility address to route
    // to. Opened from the Clinical Operations Board's roster with the facility
    // handed in through componentInputs.facilityOverride instead.
    static readonly ARSANA_VIEWS = new SlidePanelDefinition({
        key: 'ARSANA_VIEWS',
        name: {lowerCase: 'arsana views', capitalized: 'Arsana Views'},
        icon: 'book-open',
        size: 'lg',
        verticalAlign: 'top',
        permissions: ['PERMISSION_READ_ADMIN_DASHBOARD'],
        loadComponent: () => import('../portal/dashboard/admin-dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent),
        loadChildren: () => import('../portal/dashboard/admin-dashboard/admin-dashboard.module').then(m => m.AdminDashboardModule),
    });
    static readonly ACUTE_COMPLEX_PATIENTS = new SlidePanelDefinition({
        key: 'ACUTE_COMPLEX_PATIENTS',
        loadComponent: () => import('../portal/dashboard/clinical-oversight/complex-acute-patients/complex-acute-patients.component').then(m => m.ComplexAcutePatientsComponent),
        verticalAlign: 'top',
        customMarkup: true,
        size: 'md',
    });
    static readonly ACUTE_COMPLEX_PATIENT_DETAILS = new SlidePanelDefinition({
        key: 'ACUTE_COMPLEX_PATIENT_DETAILS',
        loadComponent: () => import('../portal/dashboard/clinical-oversight/complex-acute-patients/complex-acute-patient-detail/complex-acute-patient-detail.component').then(m => m.ComplexAcutePatientDetailComponent),
        verticalAlign: 'top',
        size: 'md',
    });
    static readonly CLINICAL_NOTE = new SlidePanelDefinition(ContentDefinitions.CLINICAL_NOTE, {
        key: 'CLINICAL_NOTE',
        verticalAlign: 'top',
        customMarkup: true,
        defRootUrl: 'patients',
    });
    static readonly PHYSICIAN_TEAM_SETTINGS = new SlidePanelDefinition({
        key: 'PHYSICIAN_TEAM_SETTINGS',
        path: 'admin/physician-teams/:id',
        loadChildren: () => import('../portal/admin/physician-teams/physician-team-settings/physician-team-settings.module').then(m => m.PhysicianTeamSettingsModule),
        loadComponent: () => import('../portal/admin/physician-teams/physician-team-settings/physician-team-settings.component').then(m => m.PhysicianTeamSettingsComponent),
        customMarkup: true,
        defRootUrl: 'admin/physician-teams',
        size: 'lg',
    });
    static readonly PHYSICIAN_TEAM_SETTINGS_ADD_USER = new SlidePanelDefinition({
        key: 'PHYSICIAN_TEAM_SETTINGS_ASSIGN_USER',
        path: 'admin/physician-teams/:id/edit/:facility?requestType=request_membership',
        defRootUrl: 'admin/physician-teams',
        customMarkup: true,
        loadComponent: () => import('../portal/admin/physician-teams/physician-team-settings/physician-team-settings-edit/physician-team-settings-edit.component').then(m => m.PhysicianTeamSettingsEditComponent),
        loadChildren: () => import('../portal/admin/physician-teams/physician-team-settings/physician-team-settings-edit/physician-team-settings-edit.module').then(m => m.PhysicianTeamSettingsEditModule),
    });
    static readonly PHYSICIAN_TEAM_SETTINGS_ASSIGN_USER = new SlidePanelDefinition({
        ...SlidePanelDefinitions.PHYSICIAN_TEAM_SETTINGS_ADD_USER,
        path: 'admin/physician-teams/:id/edit/:facility?requestType=request_assign',
    });
    static readonly USER_PHYSICIAN_MATRIX = new SlidePanelDefinition({
        key: 'USER_PHYSICIAN_MATRIX',
        path: 'admin/care-teams/user/:id',
        defRootUrl: 'admin/billing-groups',
        permissions: ['PERMISSION_READ_PHYSICIAN_TEAMS'],
        size: 'max',
        customMarkup: true,
        loadChildren: () => import('../portal/system-maintenance/user-physician-matrix/user-physician-matrix-panel/user-physician-matrix-panel.module').then(m => m.UserPhysicianMatrixPanelModule),
        loadComponent: () => import('../portal//system-maintenance/user-physician-matrix/user-physician-matrix.component').then(m => m.UserPhysicianMatrixComponent),
    });
    static readonly INSURANCE_HISTORY = new SlidePanelDefinition({
        key: 'INSURANCE_HISTORY',
        verticalAlign: 'top',
        customMarkup: true,
        path: 'patients/:patientId/insurance-history',
        defRootUrl: 'patients/:patientId?tab=admin',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/insurance/insurance-history/insurance-history.component').then(m => m.InsuranceHistoryComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/insurance/insurance-history/insurance-history.module').then(m => m.InsuranceHistoryModule),
    });
    static readonly INSURANCE_VERIFICATION_DETAIL = new SlidePanelDefinition({
        key: 'INSURANCE_VERIFICATION_DETAIL',
        verticalAlign: 'top',
        path: 'patients/:patientId/insurance-verifications/:id',
        defRootUrl: 'patients/:patientId/insurance-history',
        customMarkup: true,
        size: 'lg',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-insurance/patient-insurance-verification-details/patient-insurance-verification-details.component').then(m => m.PatientInsuranceVerificationDetailsComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/patient-insurance/patient-insurance-verification-details/patient-insurance-verification-details.module').then(m => m.PatientInsuranceVerificationDetailsModule),
    });
    static readonly SMART_INSURANCE_VERIFICATION_DETAIL = new SlidePanelDefinition({
        key: 'SMART_INSURANCE_VERIFICATION_DETAIL',
        verticalAlign: 'top',
        path: 'patients/:patientId/smart-verifications/:id',
        defRootUrl: 'patients/:patientId/insurance-verifications/:id',
        customMarkup: true,
        loadComponent: () => import('../portal/@portal-shared/smart-insurance-eligibility/smart-insurance-eligibility-detail/smart-insurance-eligibility-detail.component').then(m => m.SmartInsuranceEligibilityDetailComponent),
        loadChildren: () => import('../portal/@portal-shared/smart-insurance-eligibility/smart-insurance-eligibility-detail/smart-insurance-eligibility-detail.module').then(m => m.SmartInsuranceEligibilityDetailModule),
    });
    static readonly INSURANCE_EDIT = new SlidePanelDefinition({
        key: 'INSURANCE_EDIT',
        verticalAlign: 'top',
        path: 'patients/:patientId/insurance',
        defRootUrl: 'patients/:patientId?tab=admin',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/insurance/insurance-edit/insurance-edit.component').then(m => m.InsuranceEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/insurance/insurance-edit/patient-insurance-edit.module').then(m => m.InsuranceEditModule),
    });
    // Only for legacy support, should be removed in the future
    static readonly INSURANCE_EDIT_LEGACY = new SlidePanelDefinition({
        key: 'INSURANCE_EDIT_OLD',
        verticalAlign: 'top',
        path: 'patients/:patientId/insuranceOLD',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-insurance/patient-insurance-edit_LEGACY/patient-insurance-edit.component').then(m => m.PatientInsuranceEditComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/patient-insurance/patient-insurance-edit_LEGACY/patient-insurance-edit.module').then(m => m.PatientInsuranceEditModule),
    });
    static readonly INSURANCE_EDIT_NEW = new SlidePanelDefinition({
        ...this.INSURANCE_EDIT,
        key: 'INSURANCE_EDIT_NEW',
        path: 'patients/:patientId/insurance/new',
    });
    static readonly PATIENT_CLAIMS = new SlidePanelDefinition({
        key: 'PATIENT_CLAIMS',
        size: 'lg',
        verticalAlign: 'top',
        path: 'patients/:id/claims',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-claims/patient-claims.component').then(m => m.PatientClaimsComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-claims/patient-claims.module').then(m => m.PatientClaimsModule),
    });
    static readonly CLAIM = new SlidePanelDefinition(ContentDefinitions.CLAIM, {
        verticalAlign: 'top',
        customMarkup: true,
        defRootUrl: 'billing/claims',
        permissions: ['PERMISSION_WRITE_CLAIMS'],
        size: 'lg',
        loadComponent: () => import('../portal/billing/claim-details/claim-details.component').then(m => m.ClaimDetailsComponent),
        loadChildren: () => import('../portal/billing/claim-details/claim-details.module').then(m => m.ClaimDetailsModule),
    });
    static readonly CLAIM_HISTORY = new SlidePanelDefinition({
        key: 'CLAIM_HISTORY',
        path: `${SlidePanelDefinitions.CLAIM.path}/history`,
        verticalAlign: 'top',
        permissions: ['PERMISSION_WRITE_CLAIMS'],
        loadComponent: () => import('../portal/billing/claim-history/claim-history.component').then(m => m.ClaimHistoryComponent),
        loadChildren: () => import('../portal/billing/claim-history/claim-history.module').then(m => m.ClaimHistoryModule),
    });
    static readonly REMITTANCE = new SlidePanelDefinition(ContentDefinitions.REMITTANCE, {
        size: 'md',
        verticalAlign: 'top',
        customMarkup: true,
        permissions: ['PERMISSION_READ_RCM'],
        defRootUrl: 'billing/payments?tab=REMITTANCE_ADVICES',
        loadComponent: () => import('../portal/billing/remittance-details/remittance-details.component').then(m => m.RemittanceDetailsComponent),
        loadChildren: () => import('../portal/billing/remittance-details/remittance-details.module').then(m => m.RemittanceDetailsModule),
    });
    static readonly GROUP = new SlidePanelDefinition({
        key: 'GROUP',
        size: 'lg',
        path: 'system-settings/groups/:id',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/admin/groups/group-details/group-details.component').then(m => m.GroupDetailsComponent),
        loadChildren: () => import('../portal/admin/groups/group-details/group-details.module').then(m => m.GroupDetailsModule),
    });
    static readonly GROUP_EDIT = new SlidePanelDefinition({
        key: 'GROUP_EDIT',
        size: 'md',
        path: 'system-settings/groups/:id/edit',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/admin/groups/group-edit/group-edit.component').then(m => m.GroupEditComponent),
        loadChildren: () => import('../portal/admin/groups/group-edit/group-edit.module').then(m => m.GroupEditModule),
    });
    static readonly GROUP_EDIT_NEW = new SlidePanelDefinition({
        ...this.GROUP_EDIT,
        key: 'GROUP_EDIT_NEW',
        path: 'system-settings/groups/new',
    });
    static readonly CUSTOMER = new SlidePanelDefinition({
        key: 'CUSTOMER',
        name: {lowerCase: 'customer', capitalized: 'Customer'},
        path: 'system-settings/customers/:id',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/admin/customers/customer-details/customer-details.component').then(m => m.CustomerDetailsComponent),
        loadChildren: () => import('../portal/admin/customers/customer-details/customer-details.module').then(m => m.CustomerDetailsModule),
    });
    static readonly CUSTOMER_EDIT = new SlidePanelDefinition({
        key: 'CUSTOMER_EDIT',
        size: 'md',
        customMarkup: true,
        path: 'system-settings/customers/:id/edit',
        defRootUrl: 'system-settings/customers',
        loadComponent: () => import('../portal/admin/customers/customer-edit/customer-edit.component').then(m => m.CustomerEditComponent),
        loadChildren: () => import('../portal/admin/customers/customer-edit/customer-edit.module').then(m => m.CustomerEditModule),
    });
    static readonly CUSTOMER_EDIT_NEW = new SlidePanelDefinition({
        ...this.CUSTOMER_EDIT,
        key: 'CUSTOMER_EDIT_NEW',
        path: 'system-settings/customers/new',
    });
    static readonly FAX_NUMBER_HISTORY = new SlidePanelDefinition({
        key: 'FAX_NUMBER_HISTORY',
        size: 'md',
        loadComponent: () => import('../portal/admin/nursing-stations/fax-number-history/fax-number-history.component').then(m => m.FaxNumberHistoryComponent),
    });
    static readonly NURSING_STATION_EDIT = new SlidePanelDefinition({
        key: 'NURSING_STATION_EDIT',
        size: 'md',
        verticalAlign: 'top',
        path: 'admin/nursing-stations/:id',
        permissions: ['PERMISSION_WRITE_ORGANIZATION', 'PERMISSION_WRITE_FAX_NUMBER'],
        loadComponent: () => import('../portal/admin/nursing-stations/nursing-station-edit/nursing-station-edit.component').then(m => m.NursingStationEditComponent),
        loadChildren: () => import('../portal/admin/nursing-stations/nursing-station-edit/nursing-station-edit.module').then(m => m.NursingStationEditModule),
    });
    static readonly NURSING_STATION_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.NURSING_STATION_EDIT,
        key: 'NURSING_STATION_NEW',
        path: 'admin/nursing-stations/new',
    });
    static readonly PATIENT_WOUND = new SlidePanelDefinition({
        key: 'PATIENT_WOUND',
        size: 'lg',
        verticalAlign: 'top',
        customMarkup: true,
        path: 'patients/:patientId/wounds/:woundId',
        defRootUrl: 'patients',
        permissions: ['PERMISSION_READ_WOUNDS'],
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/patient-wounds/patient-wound/patient-wound-slide-panel/patient-wound-slide-panel.module').then(m => m.PatientWoundSlidePanelModule),
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-wounds/patient-wound/patient-wound-view/patient-wound.component').then(m => m.PatientWoundComponent),
    });
    static readonly CREATE_WOUND = new SlidePanelDefinition({
        ...SlidePanelDefinitions.PATIENT_WOUND,
        key: 'CREATE_WOUND',
        path: 'patients/:patientId/wounds/new',
        permissions: ['PERMISSION_WRITE_WOUNDS'],
    });
    static readonly WOUND_LIST = new SlidePanelDefinition({
        key: 'WOUND_LIST',
        path: 'patients/:id/wounds',
        size: 'md',
        verticalAlign: 'top',
        customMarkup: true,
        permissions: ['PERMISSION_READ_WOUNDS'],
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/patient-wounds/patient-wound-list/patient-wound-list-panel/patient-wound-list-panel.module').then(m => m.PatientWoundListPanelModule),
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-wounds/patient-wound-list/patient-wound-list-panel/patient-wound-list-panel.component').then(m => m.PatientWoundListPanelComponent),
    });
    static readonly WOUND_ORDER_DETAIL = new SlidePanelDefinition({
        key: 'WOUND_ORDER_DETAIL',
        path: 'wound-worklist/:id',
        size: 'lg',
        verticalAlign: 'top',
        customMarkup: true,
        permissions: ['PERMISSION_READ_WOUNDS'],
        loadChildren: () => import('../portal/wound-worklist/wound-worklist-detail/wound-worklist-detail-panel.module').then(m => m.WoundWorklistDetailPanelModule),
        loadComponent: () => import('../portal/wound-worklist/wound-worklist-detail/wound-worklist-detail.component').then(m => m.WoundWorklistDetailComponent),
    });
    static readonly PAYMENT_DETAILS = new SlidePanelDefinition({
        key: 'PAYMENT_DETAILS',
        size: 'lg',
        verticalAlign: 'top',
        path: 'billing/payments/:id',
        defRootUrl: 'billing/payments?tab=PAYMENT_LIST',
        permissions: ['PERMISSION_READ_RCM'],
        loadComponent: () => import('../portal/billing/payment-details/payment-details.component').then(m => m.PaymentDetailsComponent),
        loadChildren: () => import('../portal/billing/payment-details/payment-details.module').then(m => m.PaymentDetailsModule),
    });
    static readonly PAYMENT_DETAILS_NEW = new SlidePanelDefinition({
        ...this.PAYMENT_DETAILS,
        key: 'PAYMENT_DETAILS_NEW',
        path: 'billing/payments/new',
    });
    static readonly PART_B_WORKLIST = new SlidePanelDefinition({
        key: 'PART_B_WORKLIST',
        size: 'lg',
        verticalAlign: 'top',
        permissions: ['PERMISSION_READ_PART_B_WORKLIST_LIST', 'PERMISSION_WRITE_PART_B_WORKLIST_LIST'],
        loadChildren: () => import('../portal/part-b/part-b-worklist-list-panel/part-b-worklist-panel.module').then(m => m.PartBWorklistPanelModule),
        loadComponent: () => import('../portal/part-b/part-b-worklist/part-b-worklist.component').then(m => m.PartBWorklistComponent),
    });
    static readonly PART_B_WORKLIST_ITEM = new SlidePanelDefinition({
        key: 'PART_B_WORKLIST_ITEM',
        path: 'part-b/worklist/:id',
        size: 'lg',
        verticalAlign: 'top',
        customMarkup: true,
        permissions: ['PERMISSION_READ_PART_B_WORKLIST_LIST', 'PERMISSION_WRITE_PART_B_WORKLIST_LIST'],
        loadChildren: () => import('../portal/part-b/part-b-worklist-details/part-b-worklist-details.module').then(m => m.PartBWorklistDetailsModule),
        loadComponent: () => import('../portal/part-b/part-b-worklist-details/part-b-worklist-details.component').then(m => m.PartBWorklistDetailsComponent),
    });
    static readonly PARSED_WOUNDS = new SlidePanelDefinition({
        key: 'PARSED_WOUNDS',
        size: 'md',
        verticalAlign: 'top',
        loadChildren: () => import('../portal/@slide-panel-preview/patient-details/patient-wounds/wound-association/wound-documents/parsed-wound-list/parsed-wound-list.module').then(m => m.ParsedWoundListModule),
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-wounds/wound-association/wound-documents/parsed-wound-list/parsed-wound-list.component').then(m => m.ParsedWoundListComponent),
        customMarkup: true,
        path: 'patients/:patientId/wound-documents/:documentId',
        defRootUrl: 'patients',
    });
    static readonly PART_B_WORKLIST_ORDER_SUGGESTIONS = new SlidePanelDefinition({
        key: 'PART_B_WORKLIST_ORDER_SUGGESTIONS',
        size: 'lg',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/part-b/part-b-worklist/part-b-worklist-status-suggestions/part-b-worklist-status-suggestions.component').then(m => m.PartBWorklistStatusSuggestionsComponent),
        customMarkup: true,
    });
    static readonly PATIENT_WOUND_DOCUMENTS = new SlidePanelDefinition({
        key: 'PATIENT_WOUND_DOCUMENTS',
        size: 'md',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/@slide-panel-preview/patient-details/patient-wounds/wound-association/wound-documents/wound-documents.component').then(m => m.WoundDocumentsComponent),
        customMarkup: true,
    });
    static readonly USER_BROWSING_DETAILS = new SlidePanelDefinition({
        key: 'USER_BROWSING_DETAILS',
        size: 'lg',
        verticalAlign: 'top',
        path: 'system-settings/auth-logs/:id',
        loadComponent: () => import('../portal/admin/auth-logs/user-browsing-details/user-browsing-details.component').then(m => m.UserBrowsingDetailsComponent),
        loadChildren: () => import('../portal/admin/auth-logs/user-browsing-details/user-browsing-details.module').then(m => m.UserBrowsingDetailsModule),
    });
    static readonly FACILITY_PROPOSAL_EDIT = new SlidePanelDefinition({
        key: 'FACILITY_PROPOSAL_EDIT',
        verticalAlign: 'top',
        path: 'system-settings/facility-proposals/:id',
        loadComponent: () => import('../portal/admin/entry-proposals/facility-proposals/facility-proposal-edit/facility-proposal-edit.component').then(m => m.FacilityProposalEditComponent),
        loadChildren: () => import('../portal/admin/entry-proposals/facility-proposals/facility-proposal-edit/facility-proposal-edit.module').then(m => m.FacilityProposalEditModule),
    });
    static readonly FACILITY_PROPOSAL_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.FACILITY_PROPOSAL_EDIT,
        key: 'FACILITY_PROPOSAL_NEW',
        path: 'system-settings/facility-proposals/new',
    });

    static readonly USER_PROPOSAL_EDIT = new SlidePanelDefinition({
        key: 'USER_PROPOSAL_EDIT',
        path: 'system-settings/user-proposals/:id',
        verticalAlign: 'top',
        permissions: ['PERMISSION_WRITE_USER_PROPOSALS', 'PERMISSION_WRITE_PATIENT_PROPOSALS', 'PERMISSION_WRITE_FACILITY_PROPOSALS'],
        loadComponent: () => import('../portal/admin/entry-proposals/user-proposals/user-proposal-edit/user-proposal-edit.component').then(m => m.UserProposalEditComponent),
        loadChildren: () => import('../portal/admin/entry-proposals/user-proposals/user-proposal-edit/user-proposal-edit.module').then(m => m.UserProposalEditModule),
    });
    static readonly USER_PROPOSAL_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.USER_PROPOSAL_EDIT,
        key: 'USER_PROPOSAL_NEW',
        path: 'system-settings/user-proposals/new',
    });

    static readonly PATIENT_PROPOSAL_EDIT = new SlidePanelDefinition({
        key: 'PATIENT_PROPOSAL_EDIT',
        path: 'system-settings/patient-proposals/:id',
        verticalAlign: 'top',
        permissions: ['PERMISSION_WRITE_USER_PROPOSALS', 'PERMISSION_WRITE_PATIENT_PROPOSALS', 'PERMISSION_WRITE_FACILITY_PROPOSALS'],
        loadComponent: () => import('../portal/admin/entry-proposals/patient-proposals/patient-proposal-edit/patient-proposal-edit.component').then(m => m.PatientProposalEditComponent),
        loadChildren: () => import('../portal/admin/entry-proposals/patient-proposals/patient-proposal-edit/patient-proposal-edit.module').then(m => m.PatientProposalEditModule),
    });
    static readonly PATIENT_PROPOSAL_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.PATIENT_PROPOSAL_EDIT,
        key: 'PATIENT_PROPOSAL_NEW',
        path: 'system-settings/patient-proposals/new',
    });
    static readonly CLIENT_DETAIL = new SlidePanelDefinition({
        key: 'CLIENT_DETAIL',
        size: 'lg',
        path: 'system-settings/clients/:id',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/admin/clients/client-details/client-details.component').then(m => m.ClientDetailsComponent),
        loadChildren: () => import('../portal/admin/clients/client-details/client-details.module').then(m => m.ClientDetailsModule),
    });
    static readonly CLIENT_EDIT = new SlidePanelDefinition({
        key: 'CLIENT_EDIT',
        size: 'md',
        path: 'system-settings/clients/:id/edit',
        verticalAlign: 'top',
        loadComponent: () => import('../portal/admin/clients/client-edit/client-edit.component').then(m => m.ClientEditComponent),
        loadChildren: () => import('../portal/admin/clients/client-edit/client-edit.module').then(m => m.ClientEditModule),
    });
    static readonly CLIENT_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.CLIENT_EDIT,
        key: 'CLIENT_NEW',
        path: 'system-settings/clients/new',
    });
    static readonly ASSIGNED_BILLING_GROUP_PLAN_PATIENTS = new SlidePanelDefinition({
        key: 'ASSIGNED_BILLING_GROUP_PLAN_PATIENTS',
        size: 'md',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/admin/insurance-plan-management/assigned-billing-group-plan-patients/assigned-billing-group-plan-patients.component').then(m => m.AssignedBillingGroupPlanPatientsComponent),
    });
    static readonly RULE_EDIT = new SlidePanelDefinition({
        key: 'RULE_EDIT',
        path: 'system-settings/rules/:id/edit',
        defRootUrl: 'system-settings/rules',
        loadComponent: () => import('../portal/admin/rules/rule-edit/rule-edit.component').then(m => m.RuleEditComponent),
        loadChildren: () => import('../portal/admin/rules/rule-edit/rule-edit.module').then(m => m.RuleEditModule),
    });
    static readonly RULE_EDIT_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.RULE_EDIT,
        key: 'RULE_EDIT_NEW',
        path: 'system-settings/rules/new',
    });
    static readonly RULE_CATEGORIES = new SlidePanelDefinition({
        key: 'RULE_CATEGORIES',
        verticalAlign: 'top',
        path: 'system-settings/rules/categories',
        loadComponent: () => import('../portal/admin/rules/rule-categories/rule-categories.component').then(m => m.RuleCategoriesComponent),
        loadChildren: () => import('../portal/admin/rules/rule-categories/rule-categories.module').then(m => m.RuleCategoriesModule),
    });
    static readonly RULE_CATEGORY_EDIT = new SlidePanelDefinition({
        key: 'RULE_CATEGORY_EDIT',
        path: 'system-settings/rules/categories/:id/edit',
        defRootUrl: 'system-settings/rules',
        loadComponent: () => import('../portal/admin/rules/rule-categories/rule-category-edit/rule-category-edit.component').then(m => m.RuleCategoryEditComponent),
        loadChildren: () => import('../portal/admin/rules/rule-categories/rule-category-edit/rule-category-edit.module').then(m => m.RuleCategoryEditModule),
    });
    static readonly RULE_CATEGORY_EDIT_NEW = new SlidePanelDefinition({
        ...SlidePanelDefinitions.RULE_CATEGORY_EDIT,
        key: 'RULE_CATEGORY_EDIT_NEW',
        path: 'system-settings/rule-categories/new',
    });
    static readonly RULES_DISPLAY = new SlidePanelDefinition({
        key: 'RULES_DISPLAY',
        size: 'md',
        verticalAlign: 'top',
        customMarkup: true,
        loadComponent: () => import('../portal/@slide-panel-preview/rules-display/rules-display.component').then(m => m.RulesDisplayComponent),
    });
    static readonly PATIENT_ADMISSION_REVIEW = new SlidePanelDefinition({
        key: 'PATIENT_ADMISSION_REVIEW',
        size: 'md',
        verticalAlign: 'top',
        path: 'patients/:patientId/admission-review',
        loadChildren: () => import('../portal/@slide-panel-preview/patient-admission-review/patient-admission-review.module').then(m => m.PatientAdmissionReviewModule),
        loadComponent: () => import('../portal/@slide-panel-preview/patient-admission-review/patient-admission-review.component').then(m => m.PatientAdmissionReviewComponent),
    });
}

export const SLIDE_PANEL_ROUTES = Object.values(SlidePanelDefinitions)
    .filter(x => x.path && (x.component || x.loadChildren))
    .map((x: SlidePanelDefinition) => x.getRoute());

export class SmartLinkDefinitions {
    static readonly PATIENT = new SmartLinkDefinition(ContentDefinitions.PATIENT, {
        getLabel: x => Human.getName(x),
        getPost: x => Patient.isDeceased(x) ? '†' : null,
        getLink: x => ContentDefinitions.PATIENT.getPath((getId(x))),
        loadComponent: () => import('../portal/@slide-panel-preview/patient-preview/patient-preview.component').then(m => m.PatientPreviewComponent),
        loadChildren: () => import('../portal/@slide-panel-preview/patient-preview/patient-preview.module').then(m => m.PatientPreviewModule),
        hoverElementExtraClasses: ['patient-preview'],
    });
    static readonly LAB_TEST = new SmartLinkDefinition<LabTest>(ContentDefinitions.LAB_TEST, {
        getColorClass: x => LabTest.getColorClass(x),
        getDate: x => x.lab_report && x.lab_report.resulted_date,
        getLabel: x => x.name,
        getPost: x => x.lab_report ? `#${x.lab_report.order_id}` : '',
    });
    static readonly VITAL = new SmartLinkDefinition<CriticalVital>(ContentDefinitions.VITAL, {
        getColorClass: () => 'danger',
        getDate: x => x.recorded,
        getLabel: x => x.type?.name,
    });
    static readonly EVENT = new SmartLinkDefinition<PatientEvent>(ContentDefinitions.EVENT, {
        getColorClass: () => 'warning',
        getDate: x => x.date,
        getLabel: x => x.type[0] + x.type.slice(1).toLowerCase(),
    });
    static readonly MEDICATION = new SmartLinkDefinition<TreatmentEntry>(ContentDefinitions.MEDICATION, {
        getDate: x => x.start_date,
        getLabel: x => limitText(x.drugText, 40),
    });
    static readonly ICD_CODE = new SmartLinkDefinition<ICDCode>(ContentDefinitions.ICD_CODE, {
        getColor: x => ICDCodeGroupService.getIcdCodeColor(x),
        getPre: x => x.code,
        getLabel: x => limitText(x.description, 40),
    });
    static readonly SNOMED_CONCEPT = new SmartLinkDefinition<SnomedConcept>(ContentDefinitions.SNOMED_CONCEPT, {
        getLabel: x => limitText(x.term, 40),
    });
    static readonly DIAGNOSIS = new SmartLinkDefinition<Diagnosis>(ContentDefinitions.DIAGNOSIS, {
        getLink: x => x.icd && ContentDefinitions.PATIENT_ICD.getPath(getId(x.patient), getId(x.icd)),
        getDate: x => x.date_diagnosed,
        getColor: x => x.icd && SmartLinkDefinitions.ICD_CODE.getColor(x.icd),
        getPre: x => x.icd && SmartLinkDefinitions.ICD_CODE.getPre(x.icd),
        getLabel: x => limitText(x.diagnosis, 40),
    });
    static readonly PATIENT_ICD = new SmartLinkDefinition<Diagnosis>(ContentDefinitions.PATIENT_ICD, {
        getLink: x => ContentDefinitions.PATIENT_ICD.getPath(getId(x.patient), getId(x.icd)),
        getColor: x => SmartLinkDefinitions.ICD_CODE.getColor(x.icd),
        getPre: x => SmartLinkDefinitions.ICD_CODE.getPre(x.icd),
        getLabel: x => SmartLinkDefinitions.ICD_CODE.getLabel(x.icd),
        getTooltip: () => 'Open Dx details for Patient',
    });
    static readonly PATIENT_SNOMED = new SmartLinkDefinition<Diagnosis>(ContentDefinitions.PATIENT_SNOMED, {
        getLink: x => ContentDefinitions.PATIENT_SNOMED.getPath(getId(x.patient), getId(x.snomed_concept)),
        getColor: x => SmartLinkDefinitions.SNOMED_CONCEPT.getColor(x.snomed_concept),
        getPre: x => SmartLinkDefinitions.SNOMED_CONCEPT.getPre(x.snomed_concept),
        getLabel: x => SmartLinkDefinitions.SNOMED_CONCEPT.getLabel(x.snomed_concept),
        getTooltip: () => 'Open Dx details for Patient',
    });
    static readonly TREATMENT_RESISTANCE = new SmartLinkDefinition({
        key: 'TREATMENT_RESISTANCE',
        icon: 'exclamation-triangle',
        getLink: x => ContentDefinitions.LAB_TEST.getPath(TreatmentEntry.getResistance(x).object_id),
        getIcon: x => x.end_date ? null : 'exclamation-triangle',
        getLabel: x => TreatmentEntry.drugText(x),
        getTooltip: x => TreatmentEntry.getResistance(x).description,
    });
    static readonly OTHER_ORDER = new SmartLinkDefinition(ContentDefinitions.OTHER_ORDER, {
        getLabel: x => x.order_type,
        getTooltip: x => x.description,
        getDate: x => x.start_date,
    });
    static readonly PROCEDURE = new SmartLinkDefinition<Procedure>(ContentDefinitions.PROCEDURE, {
        getLabel: x => x.code?.name,
        getDate: x => x.performed,
    });
    static readonly IMMUNIZATION = new SmartLinkDefinition<Immunization>(ContentDefinitions.IMMUNIZATION, {
        getLabel: x => x.name, // BST TODO: add name when properly implemented
        getDate: x => x.date,
    });
    static readonly IMAGING_RESULT = new SmartLinkDefinition<ImagingResult>(ContentDefinitions.IMAGING_RESULT, {
        getLabel: x => x.code_display,
        getDate: x => x.date,
    });
    static readonly CLINICAL_RESULT = new SmartLinkDefinition<ClinicalResult>(ContentDefinitions.CLINICAL_RESULT, {
        getLabel: x => x.code_display,
        getDate: x => x.date,
    });
    static readonly ALLERGY = new SmartLinkDefinition<Allergy>(ContentDefinitions.ALLERGY, {
        getLabel: x => x.allergyText,
        getDate: x => x.start_date,
    });
    static readonly CDS_INTERVENTION = new SmartLinkDefinition(ContentDefinitions.CDS_INTERVENTION, {
        getLabel: x => x.title,
        loadComponent: () => import('../portal/admin/cds-interventions/cds-intervention/cds-intervention-preview/cds-intervention-preview.component').then(m => m.CdsInterventionPreviewComponent),
        loadChildren: () => import('../portal/admin/cds-interventions/cds-intervention/cds-intervention-preview/cds-intervention-preview.module').then(m => m.CdsInterventionPreviewModule),
    });
    static readonly CLINICAL_NOTE = new SmartLinkDefinition<ProgressNote>(ContentDefinitions.CLINICAL_NOTE, {
        getLabel: x => `${x.modified_by} - ${x.category}`,
        getDate: x => x.date,
    });
    static readonly CLAIM = new SmartLinkDefinition<Claim>(ContentDefinitions.CLAIM, {
        getLabel: x => Claim.getIdentifier(x),
    });
    static readonly REMITTANCE = new SmartLinkDefinition<Remittance>(ContentDefinitions.REMITTANCE, {
        getLabel: x => Remittance.getIdentifier(x),
        getColorClass: x => x.paid ? 'success' : 'warning',
        getTooltip: x => x.displayPaid,
    });
    static readonly WOUND = new SmartLinkDefinition<Wound>(ContentDefinitions.WOUND, {
        getLabel: x => `${x.id}`,
    });
}

export class ReviewDefinitions {
    static readonly LAB_TEST = new ReviewDefinition<LabTest>(ContentDefinitions.LAB_TEST, {
        reviewFieldKey: 'lab_test_review',
        taskTypeKey: 'LAB_REVIEW',
    });
    static readonly LAB_REPORT = new ReviewDefinition<LabReport>(ContentDefinitions.LAB_REPORT, {
        reviewFieldKey: 'lab_test_review',
        taskTypeKey: 'LAB_REVIEW',
    });
    static readonly EVENT = new ReviewDefinition(ContentDefinitions.EVENT, {
        reviewFieldKey: 'event_review',
        taskTypeKey: 'GENERAL_REVIEW',
    });
    static readonly VITAL = new ReviewDefinition(ContentDefinitions.VITAL, {
        reviewFieldKey: 'vital_review',
        taskTypeKey: 'CRITICAL_VITALS',
    });
    static readonly MEDICATION = new ReviewDefinition(ContentDefinitions.MEDICATION, {
        reviewFieldKey: 'treatment_review',
        taskTypeKey: 'RX_RECONCILE',
    });
    static readonly OTHER_ORDER = new ReviewDefinition(ContentDefinitions.OTHER_ORDER, {
        reviewFieldKey: 'order_review',
        taskTypeKey: 'GENERAL_REVIEW',
    });
    static readonly MEDICATION_INTERACTION = new ReviewDefinition(ContentDefinitions.MEDICATION_INTERACTION, {
        taskTypeKey: 'RX_RECONCILE',
        actionEntryKey: 'treatment_history',
        reviewFieldKey: 'treatment_review',
    });
    static readonly PROCEDURE = new ReviewDefinition(ContentDefinitions.PROCEDURE, {
        taskTypeKey: 'PROCEDURE',
        reviewFieldKey: 'procedure_review',
    });
    static readonly IMMUNIZATION = new ReviewDefinition(ContentDefinitions.IMMUNIZATION, {
        taskTypeKey: 'IMMUNIZATION',
        reviewFieldKey: 'immunization_review',
    });
    static readonly IMAGING_RESULT = new ReviewDefinition(ContentDefinitions.IMAGING_RESULT, {
        taskTypeKey: 'IMAGING_RESULT',
        reviewFieldKey: 'imaging_result_review',
    });
    static readonly CLINICAL_RESULT = new ReviewDefinition(ContentDefinitions.CLINICAL_RESULT, {
        taskTypeKey: 'CLINICAL_RESULT',
        reviewFieldKey: 'clinical_test_result_review',
    });
    static readonly ALLERGY = new ReviewDefinition(ContentDefinitions.ALLERGY, {
        taskTypeKey: 'ALLERGY',
        reviewFieldKey: 'patient_allergy_review',
    });
    static readonly WOUND = new ReviewDefinition(ContentDefinitions.WOUND, {
        taskTypeKey: 'WOUND',
        reviewFieldKey: 'wound_review',
    });
}

export type ContentDefinitionKey = Exclude<keyof typeof ContentDefinitions, 'prototype'>;
export type SlidePanelTypeKey = Exclude<keyof typeof SlidePanelDefinitions, 'prototype'>;
export type SmartLinkTypeKey = Exclude<keyof typeof SmartLinkDefinitions, 'prototype'>;
export type ReviewTypeKey = Exclude<keyof typeof ReviewDefinitions, 'prototype'>;

export const FAC_MENU_ITEMS: MenuItemDefinition[] = [
    {
        name: "What's New",
        keywords: ['residents', 'overview', 'labs', 'oversight', 'medical'],
        link: '/whats-new',
        icon: 'newspaper',
        permissions: ['PERMISSION_READ_CLINICAL_OVERSIGHT', 'PERMISSION_READ_CENSUS_OVERSIGHT'],
    },
    {
        name: 'Arsana Views',
        keywords: ['residents', 'overview', 'census', 'admission', 'rehospitalization', 'oversight', 'medical'],
        link: '/arsana-views',
        icon: 'book-open',
        permissions: ['PERMISSION_READ_ADMIN_DASHBOARD'],
    },
    {
        name: 'Clinical Operations Board',
        keywords: ['residents', 'census', 'occupancy', 'skilled', 'ltc', 'hospice', 'affiliate', 'facility'],
        link: '/clinical-operations-board',
        icon: 'chart-line',
        permissions: ['PERMISSION_READ_CLINICAL_OPERATIONS_DASHBOARD'],
    },
    {
        name: 'Patients',
        keywords: ['medical records', 'resident', 'residents'],
        link: '/patients',
        icon: ContentDefinitions.PATIENT.icon,
        permissions: ['PERMISSION_READ_PATIENT_LIST'],
        testId: 'sidebar-patients',
    },
    {
        name: 'User Info',
        link: '/user-information',
        icon: SlidePanelDefinitions.USER_EDIT.icon,
        roles: ['ROLE_PATIENT_REPRESENTATIVE'],
    },
    {
        name: 'Dashboard',
        link: '/patient-dashboard',
        icon: ContentDefinitions.PATIENT.icon,
        roles: ['ROLE_PATIENT'],
    },
    {
        name: 'Facility Activity',
        icon: 'building',
        link: '/facility-activity',
        children: [
            {
                name: 'Facility Index',
                keywords: ['census', 'map', 'census map', 'admission tool', 'residents', 'beds'],
                icon: 'bed',
                link: '/facility-activity/facility-index',
                permissions: ['PERMISSION_READ_CENSUS_MAP'],
            },
            {
                name: 'Census Trends',
                keywords: ['census', 'residents', 'trends', 'beds', 'executive oversight'],
                icon: 'chart-bar',
                link: '/facility-activity/census-trends',
                permissions: ['PERMISSION_READ_CENSUS_TRENDS'],
            },
            {
                name: 'Rehospitalization',
                keywords: ['census', 'residents', 'trends', 'hospital leaves', 'readmissions', 'emergency department visit', 'inpatient stays', 'in-patient stays', 'hospital transfers'],
                icon: 'ambulance',
                link: '/facility-activity/rehospitalization',
                permissions: ['PERMISSION_READ_CENSUS_TRENDS', 'PERMISSION_READ_REHOSPITALIZATION'],
            },
        ],
    },
    {
        name: 'Care Surveillance',
        icon: 'stethoscope',
        link: '/care-surveillance',
        children: [
            {
                name: 'Infection Management',
                keywords: ['infections', 'map', 'infection map'],
                icon: 'map',
                link: '/care-surveillance/infection-management',
                permissions: ['PERMISSION_READ_INFECTION_MAP'],
            },
            {
                name: 'COVID Map',
                keywords: ['covid', 'map', 'infection map'],
                icon: 'disease',
                link: '/care-surveillance/covid-map',
                permissions: ['PERMISSION_READ_COVID_MAP'],
            },
            {
                name: 'Lab Reports',
                link: '/care-surveillance/lab-reports',
                icon: ContentDefinitions.LAB_REPORT.icon,
                permissions: ['PERMISSION_READ_LAB_REPORT_LIST'],
            },
            {
                name: 'Imaging Results',
                link: '/care-surveillance/imaging-results',
                icon: ContentDefinitions.IMAGING_RESULT.icon,
                permissions: ['PERMISSION_READ_LAB_REPORT_LIST'],
            },
            {
                name: 'Culture Dashboard',
                link: '/care-surveillance/culture-specimen-dashboard',
                icon: SlidePanelDefinitions.ORGANISM.icon,
                permissions: ['PERMISSION_READ_LAB_INFECTION_CONTROL'],
            },
            {
                name: 'Medications',
                link: '/care-surveillance/medications',
                icon: ContentDefinitions.MEDICATION.icon,
                permissions: ['PERMISSION_READ_MEDICATION_LIST'],
            },
            {
                name: 'Antibiotics',
                keywords: ['trends', 'drugs'],
                icon: 'pills',
                link: '/care-surveillance/antibiotics',
                permissions: ['PERMISSION_READ_ANTIBIOTICS'],
            },
            {
                name: 'Psychotropics',
                keywords: ['trends', 'drugs', 'psychotropics'],
                icon: 'prescription-bottle',
                link: '/care-surveillance/psychotropics',
                permissions: ['PERMISSION_READ_ANTIPSYCHOTICS'],
            },
            {
                name: 'Immunizations',
                keywords: ['covid', 'immunizations', 'infection'],
                icon: 'syringe',
                link: '/care-surveillance/immunizations',
                permissions: ['PERMISSION_READ_IMMUNIZATIONS'],
            },
            {
                name: 'Infection Trends',
                icon: 'chart-line',
                link: '/care-surveillance/trends',
                permissions: ['PERMISSION_EXPERIMENTAL_FEATURES'],
                andPermissions: ['PERMISSION_READ_TRENDS'],
            },
            {
                name: 'EMR Verification',
                keywords: ['administration', 'matrix', 'medical records', 'coaching', 'infection admin tips'],
                icon: 'edit',
                link: '/care-surveillance/emr-verification',
                permissions: ['PERMISSION_READ_COACHING_INFECTION_ADMINISTRATION'],
            },
            {
                name: 'CQM Calculator',
                keywords: ['cqm', 'calculator', 'quality', 'measures', 'quality measures'],
                icon: 'notes-medical',
                link: '/care-surveillance/cqm-calculator',
                permissions: ['PERMISSION_GENERATE_QRDA'],
            },
            {
                name: 'PI Measures',
                keywords: ['pi', 'measures', 'interoperability', 'promoting'],
                icon: 'exchange',
                link: '/care-surveillance/pi-measures',
                permissions: ['PERMISSION_READ_PI_MEASURES'],
            },
        ],
    },
    {
        name: 'Documents',
        icon: 'folder-open',
        link: '/documents',
        children: [
            {
                name: 'Report Catalog',
                keywords: ['reports', 'download', 'interact', 'infections', 'census', 'print'],
                link: '/documents/reports',
                icon: 'print',
                permissions: ['PERMISSION_READ_FACILITY', 'PERMISSION_READ_CENSUS_TRENDS'],
            },
            {
                name: 'Document Storage',
                keywords: ['uploaded reports', 'download'],
                link: '/documents/storage',
                icon: 'hdd',
                permissions: ['PERMISSION_READ_UPLOADED_REPORTS', 'PERMISSION_WRITE_UPLOADED_REPORTS'],
            },
            {
                name: 'Reference Manual',
                link: 'https://drive.google.com/file/d/1SG7IEhq6KwfwwhcJUTAoLnf-_6kLaOt0/view',
                icon: 'book-open',
                hosts: ['CORELAB'],
            },
        ],
    },
    // {
    //     name: 'Coaching',
    //     icon: 'whistle',
    //     children: [
    //     ]
    // },
    {
        name: 'Admin',
        icon: 'pencil-alt',
        link: '/admin',
        children: [
            {
                name: 'Users',
                link: '/admin/users',
                icon: 'user',
                permissions: ['PERMISSION_WRITE_USER', 'PERMISSION_READ_USER_LIST'],
            },
            {
                name: 'Billing Info Requests',
                link: '/admin/billing-info-requests',
                keywords: ['billing info', 'billing info requests', 'billing'],
                icon: 'address-book',
                permissions: ['PERMISSION_APPROVE_BILLING_INFO_REQUESTS'],
            },
            {
                name: 'Physician Teams',
                link: '/admin/physician-teams',
                icon: 'user-md',
                permissions: ['PERMISSION_READ_PHYSICIAN_TEAMS'],
            },
            {
                name: 'Locations',
                link: '/admin/locations',
                icon: 'map-marker',
                keywords: ['map', 'edit beds'],
                permissions: ['PERMISSION_READ_LOCATIONS', 'PERMISSION_WRITE_LOCATIONS'],
            },
            {
                name: 'Nursing Stations',
                link: '/admin/nursing-stations',
                icon: 'fax',
                keywords: ['fax', 'faxing', 'nurse'],
                permissions: ['PERMISSION_WRITE_ORGANIZATION', 'PERMISSION_WRITE_FAX_NUMBER'],
            },
            {
                name: 'Patient Information Audit',
                link: '/admin/patient-information-audit',
                keywords: ['patients', 'audit'],
                icon: ContentDefinitions.PATIENT.icon,
                permissions: ['PERMISSION_READ_PATIENT_INFORMATION_AUDIT'],
            },
            {
                name: 'Patient Access Management',
                link: '/admin/patient-access-management',
                keywords: ['patients', 'access'],
                icon: 'edit',
                permissions: ['PERMISSION_DECIDE_ACCESS_PERMISSIONS', 'PERMISSION_REQUEST_ACCESS_PERMISSIONS'],
            },
            {
                name: 'Initial Import',
                link: '/admin/initial-import/list',
                keywords: ['import', 'initial'],
                icon: 'play',
                permissions: ['PERMISSION_READ_INITIAL_IMPORT'],
            },
        ],
    },
];

export const OTHER_MENU_ITEMS: MenuItemDefinition[] = [
    {
        name: 'Notes',
        icon: ContentDefinitions.TASK.icon,
        link: '/arsana-notes',
        children: [
            {
                name: 'Personal',
                keywords: ['follow issues', 'following', 'ccm tasks', 'ccm notes', 'progress notes'],
                link: '/arsana-notes/personal',
                icon: 'user',
                permissions: ['PERMISSION_FOLLOW_PATIENTS'],
                exactMatch: true,
                footerNavPosition: 0,
                footerNavPriority: 0,
                alternativeIcon: ContentDefinitions.TASK.icon,
            },
            {
                name: 'Facility Inbox',
                keywords: ['follow issues', 'following', 'ccm tasks', 'ccm notes', 'actions', 'new orders', 'progress notes'],
                link: '/arsana-notes/facility-inbox',
                icon: 'inbox',
                permissions: ['PERMISSION_READ_FACILITY_TASKS'],
            },
            {
                name: 'Team Notes',
                keywords: ['follow issues', 'following', 'ccm tasks', 'ccm notes', 'actions', 'new orders', 'progress notes'],
                link: '/arsana-notes/team-notes',
                icon: 'users',
                permissions: ['PERMISSION_READ_TEAM_NOTES'],
            },
            {
                name: 'Orders',
                keywords: ['follow issues', 'following', 'ccm tasks', 'ccm notes', 'actions', 'new orders', 'progress notes', 'broadstreet orders', 'arsana orders', 'visits', 'rx orders', 'lab orders', 'other orders', 'e-prescribing'],
                link: '/arsana-notes/orders',
                icon: 'edit',
                permissions: ['PERMISSION_READ_FACILITY_TASKS', 'PERMISSION_READ_TEAM_NOTES'],
            },
            {
                name: 'Rx Worklist',
                keywords: ['rx orders', 'e-scribing', 'e-prescriptions', 'e-prescribing', 'work list'],
                link: '/arsana-notes/rx-worklist',
                icon: 'list',
                permissions: ['PERMISSION_READ_E_PRESCRIBING_WORKLIST'],
            },
        ],
    },
    {
        name: 'Part B',
        icon: 'print',
        link: '/part-b',
        children: [
            {
                name: 'Part B Dashboard',
                keywords: ['orders', 'part b worklist'],
                link: '/part-b/dashboard',
                icon: 'table',
                permissions: ['PERMISSION_READ_PART_B_DASHBOARD'],
            },
            {
                name: 'Part B Productivity',
                keywords: ['time tracking', 'productivity', 'time logs'],
                link: '/part-b/productivity',
                icon: 'stopwatch',
                permissions: ['PERMISSION_READ_PART_B_PRODUCTIVITY'],
            },
            {
                name: 'Part B Scheduler',
                keywords: ['part b worklist', 'calendar', 'visits', 'events'],
                link: '/part-b/scheduler',
                icon: 'calendar',
                permissions: ['PERMISSION_READ_SCHEDULER_EVENTS'],
                footerNavPosition: 1,
                footerNavPriority: 1,
            },
            {
                name: 'Part B Worklist',
                keywords: ['orders download'],
                link: '/part-b/worklist',
                icon: 'list',
                permissions: ['PERMISSION_READ_PART_B_WORKLIST_LIST', 'PERMISSION_WRITE_PART_B_WORKLIST_LIST'],
            },
            {
                name: 'Part B Report',
                keywords: ['orders download', 'part b worklist'],
                link: '/part-b/report',
                icon: 'download',
                permissions: ['PERMISSION_READ_ORDERS_REPORT'],
            },
        ],
    },
    {
        name: 'Lab Testing',
        icon: 'vials',
        link: '/lab-testing',
        children: [
            {
                name: 'Lab Worklist',
                keywords: ['lab orders', 'lab worklist', 'lab work list'],
                link: '/lab-testing/worklist',
                icon: 'list',
                permissions: ['PERMISSION_READ_LAB_WORKLIST'],
            },
            {
                name: 'On-Premise Lab Reports',
                keywords: ['on premise', 'piccolo', 'medonic', 'lab reports'],
                link: '/lab-testing/on-premise-reports',
                icon: 'map-marker-edit',
                permissions: ['PERMISSION_READ_LAB_WORKLIST'],
            },
        ],
    },
    {
        name: 'Wound Worklist',
        keywords: ['dressings', 'wounds', 'worklist'],
        link: '/wound-worklist',
        icon: 'band-aid',
        permissions: ['PERMISSION_READ_WOUNDS'],
    },
    {
        name: 'Applications',
        keywords: ['applications'],
        link: '/applications',
        icon: 'cogs',
        permissions: ['PERMISSION_READ_APPLICATIONS'],
    },
    {
        name: 'Download Queue',
        keywords: ['document', 'download', 'report', 'generate'],
        link: '/documents/download-queue',
        icon: 'file',
        permissions: ['PERMISSION_WRITE_REPORT_QUEUE'],
    },
    {
        name: 'COVID Testing',
        keywords: ['labs', 'shipment', 'coronavirus', 'tests', 'covid-19', 'boxes', 'bags', 'vials'],
        link: '/covid-testing',
        icon: 'vials',
        permissions: ['PERMISSION_READ_COVID_TESTING'],
    },
    {
        name: 'Scheduler',
        icon: 'calendar',
        link: '/scheduler',
        children: [
            {
                name: 'Calendar',
                keywords: ['visits', 'in-patient', 'list', 'calendar', 'visit planner'],
                link: '/scheduler/calendar',
                exactMatch: true,
                // queryParams: {onCall: false},
                icon: 'calendar',
                permissions: ['PERMISSION_READ_VISITS'],
                footerNavPosition: 1,
                footerNavPriority: 0,
            },
            {
                name: 'Next Visit Scheduler',
                keywords: ['visits', 'in-patient', 'list', 'calendar', 'visit planner'],
                link: '/scheduler/next-visits',
                icon: 'history',
                permissions: ['PERMISSION_READ_NEXT_VISIT_SCHEDULER'],
            },
            {
                name: 'Scheduler Worklist',
                keywords: ['visits', 'in-patient', 'list', 'calendar', 'visit planner', 'patient worklists'],
                link: '/scheduler/scheduler-worklist',
                icon: 'user-md',
                permissions: ['PERMISSION_READ_SCHEDULER_WORKLIST'],
            },
        ],
    },
    {
        name: 'Billing',
        icon: 'receipt',
        link: '/billing',
        children: [
            {
                name: 'Overview',
                keywords: ['cms billing', 'ccm overview', 'ccm billing', 'task', 'reporting', 'productivity'],
                link: '/billing/overview',
                icon: 'stopwatch',
                permissions: ['PERMISSION_READ_PRODUCTIVITY_REPORT', 'PERMISSION_READ_CCM_BILLING'],
                excludePermissions: ['RESTRICTION_NO_VCM_BILLING'],
            },
            {
                name: 'VCM Monthly Billing',
                keywords: ['task', 'reporting', 'summary'],
                link: '/billing/vcm-monthly-billing',
                icon: 'tasks',
                permissions: ['PERMISSION_READ_CCM_BILLING', 'PERMISSION_READ_VCM_BILLING_AUDIT'],
                excludePermissions: ['RESTRICTION_NO_VCM_BILLING'],
            },
            {
                name: 'Visit Productivity',
                keywords: ['task', 'reporting', 'summary'],
                link: '/billing/visit-productivity',
                icon: 'history',
                permissions: ['PERMISSION_READ_VISIT_BILLING_AUDIT'],
            },
            {
                name: 'Visit Billing',
                keywords: ['task', 'reporting', 'summary'],
                link: '/billing/visit-billing',
                icon: 'tasks',
                permissions: ['PERMISSION_READ_VISIT_BILLING'],
            },
            {
                name: 'Lab Billing',
                keywords: ['lab', 'billing', 'summary'],
                link: '/billing/lab-billing',
                icon: 'tasks',
                permissions: ['PERMISSION_READ_LAB_BILLING'],
            },
            {
                name: "Patients' Billing Info",
                keywords: ['patients', 'billing', 'ccm'],
                link: '/billing/patients-billing-info',
                icon: 'address-book',
                permissions: ['PERMISSION_SEND_BILLING_INFO_REQUESTS'],
            },
            {
                name: 'Task History',
                keywords: ['tasks', 'billing', 'ccm', 'notes'],
                link: '/billing/task-history',
                icon: 'history',
                permissions: ['PERMISSION_READ_TASK_HISTORY'],
            },
            {
                name: 'Claims',
                keywords: ['claims', 'billing', 'claim'],
                link: '/billing/claims',
                icon: ContentDefinitions.CLAIM.icon,
                permissions: ['PERMISSION_READ_CLAIMS', 'PERMISSION_WRITE_CLAIMS'],
            },
            {
                name: 'Payments',
                keywords: ['claims', 'billing', 'claim', 'remittance', 'advice', 'payment', 'payments'],
                link: '/billing/payments',
                icon: ContentDefinitions.REMITTANCE.icon,
                //TODO: fix permissions
                permissions: ['PERMISSION_READ_RCM'],
            },
            {
                name: 'RCM Dashboard',
                keywords: ['claims', 'billing', 'claim', 'remittance', 'rcm', 'dashboard', 'revenue cycle management'],
                link: '/billing/rcm-dashboard',
                icon: 'chart-line',
                permissions: ['PERMISSION_READ_RCM'],
            },
            {
                name: 'Insurance Plan Management',
                keywords: ['insurance', 'plan', 'management', 'customer'],
                link: '/billing/insurance-plan-management',
                icon: 'shield-check',
                permissions: ['PERMISSION_WRITE_BILLING_GROUP_PLAN'],
            },
        ],
    },
    {
        name: 'Document Inbox',
        keywords: ['clinical', 'documents', 'document', 'assign', 'ccd', 'ccda', 'c-cda', 'inbox'],
        link: '/documents/clinical-documents',
        icon: 'file-alt',
        permissions: ['PERMISSION_READ_CLINICAL_DOCUMENTS', 'PERMISSION_WRITE_CLINICAL_DOCUMENTS'],
    },
    {
        name: 'System Settings',
        icon: 'cogs',
        link: '/system-settings',
        children: [
            {
                name: 'Clients',
                keywords: ['clients', 'groups'],
                link: '/system-settings/clients',
                icon: 'building',
                permissions: ['PERMISSION_READ_CLIENTS'],
            },
            {
                name: 'Organizations',
                keywords: ['facilities', 'facility edit'],
                link: '/system-settings/organizations',
                icon: 'building',
                permissions: ['PERMISSION_WRITE_ORGANIZATION'],
            },
            {
                name: 'Groups',
                keywords: ['edit teams'],
                link: '/system-settings/groups',
                icon: 'users',
                permissions: ['PERMISSION_WRITE_GROUPS'],
            },
            {
                name: 'Customers',
                keywords: ['customers', 'customer edit', 'edit teams'],
                link: '/system-settings/customers',
                icon: 'briefcase',
                permissions: ['PERMISSION_READ_CUSTOMERS', 'PERMISSION_WRITE_CUSTOMERS'],
            },
            {
                name: 'Billing Groups',
                keywords: ['customers', 'customer edit', 'edit teams', 'claims', 'claim settings', 'claim charge amounts'],
                link: '/system-settings/billing-groups',
                icon: 'users',
                permissions: ['PERMISSION_READ_BILLING_GROUP', 'PERMISSION_WRITE_BILLING_GROUP'],
            },
            {
                name: 'Facility Proposals',
                keywords: ['organizations', 'facility edit', 'add facilities'],
                link: '/system-settings/facility-proposals',
                icon: 'building',
                permissions: ['PERMISSION_READ_FACILITY_PROPOSALS'],
            },
            {
                name: 'User Proposals',
                keywords: ['edit user', 'create user', 'add users'],
                link: '/system-settings/user-proposals',
                icon: 'user',
                permissions: ['PERMISSION_READ_USER_PROPOSALS'],
            },
            {
                name: 'Patient Proposals',
                keywords: ['patient', 'patient edit', 'add patient', 'create patient'],
                link: '/system-settings/patient-proposals',
                icon: 'hospital-user',
                permissions: ['PERMISSION_READ_PATIENT_PROPOSALS'],
            },
            {
                name: 'Import Accounts',
                link: '/system-settings/import-accounts',
                icon: 'key',
                permissions: ['PERMISSION_READ_IMPORT_ACCOUNTS_LIST'],
            },
            {
                name: 'Manual Import Dashboard',
                link: '/system-settings/manual-import-dashboard',
                icon: 'tasks',
                roles: ['ROLE_SUPER_ADMIN'],
                permissions: ['PERMISSION_READ_IMPORT'],
            },
            {
                name: 'Import',
                link: '/system-settings/import',
                icon: 'database',
                permissions: ['PERMISSION_READ_IMPORT'],
            },
            {
                name: 'Auth Logs',
                link: '/system-settings/auth-logs',
                icon: 'users',
                permissions: ['PERMISSION_READ_LOGS'],
            },
            {
                name: 'Billing Codes',
                keywords: ['ccm', 'mac localities', 'tasks', 'prices'],
                link: '/system-settings/billing-codes',
                icon: 'receipt',
                permissions: ['PERMISSION_READ_BILLING_CODES'],
            },
            {
                name: 'Drug Categories',
                link: '/system-settings/drug-categories',
                keywords: ['drugs', 'categorization'],
                icon: 'pills',
                permissions: ['PERMISSION_WRITE_DRUG_CATEGORIES'],
            },
            {
                name: 'Diagnosis Categories',
                link: '/system-settings/diagnosis-categories',
                keywords: ['diagnoses', 'icd codes', 'icd grouping', 'dx', 'categorization'],
                icon: 'diagnoses',
                permissions: ['PERMISSION_WRITE_DIAGNOSIS_CATEGORIES'],
            },
            {
                name: 'Psychotropic Group Settings',
                link: '/system-settings/psychotropic-group-settings',
                keywords: ['psychotropic', 'antipsychotic', 'group'],
                icon: 'prescription-bottle',
                permissions: ['PERMISSION_WRITE_PSYCHOTROPIC_GROUPS'],
            },
            {
                name: 'CCM Task Categories',
                link: '/system-settings/ccm-task-categories',
                keywords: [],
                icon: 'stopwatch',
                permissions: ['PERMISSION_WRITE_TASK_TYPES'],
            },
            {
                name: 'System Messages',
                link: '/system-settings/system-alerts',
                keywords: ['system alerts', 'system messages', 'system', 'alerts', 'alert', 'messages', 'message'],
                icon: 'megaphone',
                permissions: ['PERMISSION_WRITE_SYSTEM_ALERTS'],
            },
            {
                name: 'Person Management',
                link: '/system-settings/person-management',
                keywords: ['persons', 'people', 'patients'],
                icon: 'check',
                permissions: ['PERMISSION_CONFIRM_PERSON_PROPOSAL', 'PERMISSION_READ_PERSONS'],
            },
            {
                name: 'App Management',
                link: '/system-settings/app-management',
                keywords: ['app'],
                icon: 'receipt',
                permissions: ['PERMISSION_WRITE_APPLICATIONS'], //PERMISSIONS_READ_APPLICATIONS
            },
            {
                name: 'CDS Interventions',
                link: '/system-settings/cds-interventions',
                keywords: ['cds', 'intervention', 'clinical decision support'],
                icon: 'comment-exclamation',
                permissions: ['PERMISSION_WRITE_CDS'],
            },
            {
                name: 'Labels',
                link: '/system-settings/labels-manager',
                icon: 'tags',
                permissions: ['PERMISSION_WRITE_LABELS'],
            },
            {
                name: 'Level of Care',
                link: '/system-settings/level-of-care',
                icon: 'file-invoice-dollar',
                permissions: ['PERMISSION_WRITE_LEVEL_OF_CARE_PAYERS'],
                keywords: ['payers', 'insurance', 'insurance companies', 'insurance payers', 'insurance payor', 'insurance payors', 'insurance payer'],
            },
            {
                name: 'Medicaid Prices',
                link: '/system-settings/medicaid-prices',
                icon: 'dollar-sign',
                permissions: ['PERMISSION_READ_MEDICAID_PRICE_SETTINGS'],
                keywords: ['medicaid', 'prices', 'price settings', 'price', 'settings'],
            },
            {
                name: 'Rules',
                link: '/system-settings/rules',
                icon: 'clipboard-list',
                permissions: ['PERMISSION_READ_RULES', 'PERMISSION_WRITE_RULES'],
                keywords: ['rules', 'rule', 'guidance', 'rule categories'],
            },
        ],
    },
    {
        name: 'System Maintenance',
        icon: 'toolbox',
        link: '/system-maintenance',
        children: [
            {
                name: 'HL7 Dashboard',
                link: '/system-maintenance/hl7',
                queryParams: {tab: 'dashboard'},
                icon: 'info-circle',
                permissions: ['PERMISSION_READ_HL7_MESSAGES', 'PERMISSION_WRITE_HL7_MESSAGES'],
            },
            {
                name: 'Lab Dashboard',
                link: '/system-maintenance/lab-dashboard',
                icon: 'vials',
                permissions: ['PERMISSION_READ_HL7_MESSAGES', 'PERMISSION_WRITE_HL7_MESSAGES'],
            },
            {
                name: 'Physician Team Management',
                link: '/system-maintenance/physician-team-management',
                icon: 'user-md',
                permissions: ['PERMISSION_READ_PHYSICIAN_TEAMS'],
                keywords: ['physician teams', 'care teams', 'physicians'],
            },
            {
                name: 'EMR Uploads',
                link: '/system-maintenance/emr-uploads',
                icon: 'file-upload',
                permissions: ['PERMISSION_READ_EMR_UPLOADS', 'PERMISSION_WRITE_EMR_UPLOADS'],
            },
            {
                name: 'Merge Patients',
                link: '/system-maintenance/patients-merge',
                icon: 'code-merge',
                permissions: ['PERMISSION_MERGE_PATIENTS'],
            },
            {
                name: 'Import Errors',
                link: '/system-maintenance/import-errors',
                icon: 'fax',
                permissions: ['PERMISSION_READ_IMPORT_ERRORS'],
            },
            {
                name: 'Census Status Check',
                link: '/system-maintenance/census-status-check',
                icon: 'info',
                permissions: ['PERMISSION_UPDATE_CENSUS'],
            },
            {
                name: 'System Operations',
                link: '/system-maintenance/system-operations',
                icon: 'wrench',
                permissions: ['PERMISSION_READ_SYSTEM_OPERATIONS'],
            },
            {
                name: 'eRx Origins',
                link: '/system-maintenance/erx-origins',
                icon: 'list',
                keywords: ['erx', 'origins', 'erx origins'],
                permissions: ['PERMISSION_READ_ERX_ORIGINS'],
            },
        ],
    },
];

export const EXTRA_MENU_ITEMS = [
    {
        name: 'Import Schedule',
        keywords: ['data import', 'schedule'],
        link: '/import/schedule',
        icon: 'calendar',
        permissions: ['PERMISSION_READ_IMPORT'],
    },
    {
        name: 'Add New User',
        keywords: ['create user'],
        link: '/admin/users/new',
        icon: 'user-plus',
        permissions: ['PERMISSION_WRITE_USER'],
    },
    {
        name: 'EMR Upload Configurations',
        keywords: ['emr upload configurations'],
        link: '/system-maintenance/emr-uploads-configurations',
        icon: 'info-circle',
        permissions: ['PERMISSION_READ_EMR_UPLOADS', 'PERMISSION_WRITE_EMR_UPLOADS'],
    },
    {
        name: 'HL7 Messages',
        keywords: ['hl7', 'messages'],
        link: '/system-maintenance/hl7',
        queryParams: {tab: 'messages'},
        icon: 'inbox',
        permissions: ['PERMISSION_READ_EMR_UPLOADS', 'PERMISSION_WRITE_EMR_UPLOADS'],
    },
];
